import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Eye, Target, Calendar, Users, Shield, CheckCircle, AlertCircle, TrendingUp, BookOpen, Brain, Clock } from 'lucide-react';
import { formatLastAnalysisDate } from '../utils/dateFormatter';
import { useLexLastSync } from '../contexts/LexLastSyncContext';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, ResponsiveContainer, Legend, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { getRoadmapPmoState, saveRoadmapPmoState } from '../services/roadmapPmoService';

interface GouvernanceProgrammationIAModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// ============================================================================
// DONNÉES — 8 AXES GOUVERNANCE PROGRAMMATION & IA (cadre holistique)
// ============================================================================
const axesProgIA = [
  {
    id: 1,
    nom: 'Vision & Stratégie',
    avatars: ['Eric Durand', 'Dany Bourassa'],
    sousPiliers: ['Direction stratégique', 'Alignement création de richesse', 'Capacité de dire non', 'Planification annuelle'],
    intention: 'Définir, communiquer et maintenir une vision claire pour le département Programmation & IA.',
    pourquoiCritique: 'Sans vision claire, le département exécute sans cap — résultats so-so.',
    concretementBZ: 'Créer de la richesse. BZ ou externe. Structurer, optimiser, valeur mesurable. ROI rapide et efficace.',
    erreursFrequentes: [
      'Exécuter sans cap — pas de vision claire',
      'Vision floue ou contradictoire',
      'Désalignement avec la création de richesse (BZ ou clients)',
      'Vision non communiquée à l\'équipe'
    ],
    niveaux: [
      { niveau: 0, label: 'Inexistant', description: 'Aucune vision formalisée' },
      { niveau: 1, label: 'POC', description: 'Vision documentée mais peu communiquée' },
      { niveau: 2, label: 'Alignée leaders', description: 'Vision partagée avec les leaders' },
      { niveau: 3, label: 'Intégrée planification', description: 'Vision intégrée dans la planification annuelle' },
      { niveau: 4, label: 'Vision vivante', description: 'Vision régulièrement rafraîchie, guide les décisions' },
      { niveau: 5, label: 'ADN organisationnel', description: 'Vision intégrée à l\'ADN, différenciateur reconnu' }
    ],
    aFaireMaintenant: [
      'Formuler la vision du département Prog en 1 minute',
      'Définir 3 priorités stratégiques non négociables (création richesse, ROI, excellence)',
      'Refuser tout ce qui n\'aligne pas la vision'
    ],
    preuvesVisibles: [
      'Vision versionnée',
      'Pitch standard Prog',
      'Objectifs reliés aux 8 axes'
    ]
  },
  {
    id: 2,
    nom: 'Gouvernance & rôles',
    avatars: ['Eric Durand', 'Guillaume Cloutier'],
    sousPiliers: ['RACI clair', 'Décisions documentées', 'Escalade définie', 'Rituels réguliers'],
    intention: 'Structurer les décisions, les rôles et les mécanismes d\'escalade.',
    pourquoiCritique: 'Sans gouvernance claire, le département devient incohérent et énergivore.',
    concretementBZ: 'Eric Durand (A). Décisions documentées. Rôles clairs. Escalade définie.',
    erreursFrequentes: [
      'Gouvernance = bureaucratie',
      'Décisions non documentées',
      'Rôles flous',
      'Mauvaise escalade'
    ],
    niveaux: [
      { niveau: 0, label: 'Ad-hoc', description: 'Décisions ad-hoc, rôles non définis' },
      { niveau: 1, label: 'Informelle', description: 'Quelques personnes prennent des décisions' },
      { niveau: 2, label: 'Comité formé', description: 'Rôles de base définis, Eric (A) identifié' },
      { niveau: 3, label: 'Cadence + décisions', description: 'Cadence régulière, décisions documentées, RACI partiel' },
      { niveau: 4, label: 'RACI complet', description: 'RACI affiché, escalade claire' },
      { niveau: 5, label: 'Auto-gouvernance', description: 'Décisions rapides, équipe autonome' }
    ],
    aFaireMaintenant: [
      'Charte gouvernance Prog (1 page)',
      'Bâtir le roadmap annuel 2026',
      'Calendrier rituels annuel'
    ],
    preuvesVisibles: [
      'Registre décisions',
      'RACI affiché',
      'Agenda type'
    ]
  },
  {
    id: 3,
    nom: 'Rentabilité & Rendement',
    avatars: ['Eric Durand', 'Guillaume Cloutier'],
    intention: 'Piloter le département par les chiffres : revenus par programmeur, marge, charge utile.',
    pourquoiCritique: 'Sans chiffres, pas de pilotage — résultats so-so.',
    concretementBZ: '150k$/programmeur · marge · charge utile · suivi mensuel.',
    erreursFrequentes: [
      'Pas de suivi financier',
      'Charge non mesurée',
      'Rentabilité floue'
    ],
    niveaux: [
      { niveau: 0, label: 'Chaotique', description: 'Aucun suivi, chiffres inconnus' },
      { niveau: 1, label: 'Partiel', description: 'Quelques métriques, suivi irrégulier' },
      { niveau: 2, label: 'Objectif 150k$', description: 'Objectif défini, suivi trimestriel' },
      { niveau: 3, label: 'Suivi mensuel', description: 'Chiffres mensuels, marge et charge mesurées' },
      { niveau: 4, label: 'Prédictif', description: 'Prévisions fiables, actions correctives rapides' },
      { niveau: 5, label: 'Excellence financière', description: 'Rentabilité durable, pilotage data-driven' }
    ],
    aFaireMaintenant: [
      'Définir et suivre la métrique 150k$/programmeur',
      'Suivi mensuel marge et charge utile',
      'Rituel financier mensuel (dashboard)'
    ],
    preuvesVisibles: [
      'Dashboard financier',
      'Revenus/prog mesurés',
      'Marge suivie'
    ]
  },
  {
    id: 4,
    nom: 'RH & Équipe',
    avatars: ['Eric Durand', 'Dany Bourassa'],
    sousPiliers: ['Onboarding structuré', 'Feedback continu (1:1)', 'Parcours carrière', 'Rétention et culture'],
    intention: 'Recruter, fidéliser, développer — équipe de 6, culture forte. On travaille avec PRIsME Employé.',
    pourquoiCritique: 'Sans équipe engagée, pas de livraison durable.',
    concretementBZ: 'PRIsME Employé · onboarding clair · rétro régulières · parcours de carrière · rétention mesurée.',
    erreursFrequentes: [
      'Onboarding informel',
      'Pas de feedback continu',
      'Rétention ignorée'
    ],
    niveaux: [
      { niveau: 0, label: 'Ad-hoc', description: 'Gestion informelle, turnover élevé' },
      { niveau: 1, label: 'Onboarding basique', description: 'Quelques processus, rôles flous' },
      { niveau: 2, label: 'Rôles clairs', description: 'Rôles définis, rétro régulières' },
      { niveau: 3, label: 'Feedback continu', description: 'Parcours carrière, feedback structuré' },
      { niveau: 4, label: 'Culture forte', description: 'Rétention mesurée, équipe engagée' },
      { niveau: 5, label: 'Équipe référence', description: 'Talent attiré, excellence reconnue' }
    ],
    aFaireMaintenant: [
      'PRIsME Employé à jour pour rôles Prog',
      'Onboarding documenté (checklist)',
      'Rétro 1:1 régulières',
      'Parcours carrière identifié'
    ],
    preuvesVisibles: [
      'PRIsME Employé utilisé',
      'Taux rétention',
      'Équipe stable (6)'
    ]
  },
  {
    id: 5,
    nom: 'Charge projet & Client',
    avatars: ['Guillaume Cloutier', 'Marc-Antoine Caron'],
    sousPiliers: ['Owner par client/app', 'PRIsME Client à jour', 'Prise en charge documentée', 'Suivi et livraison', 'Vente & Commercialisation'],
    intention: 'Le Prime de la prog — qui porte la responsabilité du client, du projet ou de l\'application. On travaille avec PRIsME Client.',
    pourquoiCritique: 'Sans owner clair, le client et l\'app se perdent.',
    concretementBZ: 'PRIsME Client · owner par client/app · prise en charge documentée · suivi régulier · livrables clients.',
    erreursFrequentes: [
      'Pas d\'owner identifié',
      'Client sans suivi',
      'Abandon post-livraison'
    ],
    niveaux: [
      { niveau: 0, label: 'Absent', description: 'Pas de prise en charge structurée' },
      { niveau: 1, label: 'Informel', description: 'Quelques personnes s\'occupent naturellement' },
      { niveau: 2, label: 'Rôles définis', description: 'Charge projet / owner identifiés' },
      { niveau: 3, label: 'Référent par client', description: 'Chaque client/app a un responsable clair' },
      { niveau: 4, label: 'Processus cadré', description: 'Kickoff, suivi, livraison, maintenance documentés' },
      { niveau: 5, label: 'Référence', description: 'Lien client fluide, renouvellement et croissance mesurés' }
    ],
    aFaireMaintenant: [
      'Identifier owner par client/app (Prime de la prog)',
      'PRIsME Client à jour par mandat',
      'Documenter prise en charge et suivi',
      'Rituel suivi client régulier'
    ],
    preuvesVisibles: [
      'Owner affiché par client',
      'PRIsME Client utilisé',
      'Livrables clients documentés'
    ]
  },
  {
    id: 6,
    nom: 'Technique & Livraison',
    avatars: ['Guillaume Cloutier', 'Marc-Antoine Caron'],
    sousPiliers: ['Code & standards (DoD)', 'Sécurité by design', 'Infra & DevOps (CI/CD)', 'CodeHub adoption'],
    intention: 'Programmation, Sécurité, Infra, Livraison, Outils, IA — tout ce qui fait qu\'on livre des applications.',
    pourquoiCritique: 'Sans excellence technique, pas de crédibilité.',
    concretementBZ: 'Qualité code · Sécurité by design · Infra stable · Livraison reproductible · CodeHub 100% · IA intégrée.',
    erreursFrequentes: [
      'Code sans standards',
      'Sécurité en afterthought',
      'Livraison sans QA',
      'CodeHub non adopté'
    ],
    niveaux: [
      { niveau: 0, label: 'Chaotique', description: 'Code instable, pas de QA, infra manuelle' },
      { niveau: 1, label: 'Basique', description: 'Standards partiels, QA ponctuelle, quelques outils' },
      { niveau: 2, label: 'Structuré', description: 'DoD claire, QA systématique, CodeHub adopté par la majorité' },
      { niveau: 3, label: 'Reproductible', description: 'Livraisons reproductibles, sécurité intégrée, CI/CD' },
      { niveau: 4, label: 'Excellence', description: 'CodeHub 100%, IA dans livrables, monitoring' },
      { niveau: 5, label: 'Référence', description: 'Excellence reproductible, avantage concurrentiel IA' }
    ],
    aFaireMaintenant: [
      'Definition of Done claire (qualité code, QA)',
      'CodeHub adoption 100% par l\'équipe',
      'Sécurité by design dans les apps',
      'IA intégrée dans livrables (standards)'
    ],
    preuvesVisibles: [
      'QA pass rate',
      'Incidents ↓',
      'CodeHub 100% adopté',
      'Livraisons à temps'
    ]
  },
  {
    id: 7,
    nom: 'Innovation & IA',
    avatars: ['Eric Durand', 'Guillaume Cloutier', 'Marc-Antoine Caron'],
    sousPiliers: ['Humain garde le contrôle', 'IA dans livrables', 'Automatisation intelligente', 'Respect du cadre RMF', 'Expérimentations contrôlées'],
    intention: 'Intégrer l\'IA et l\'automatisation de façon contrôlée — créer un avantage concurrentiel durable sans chaos. L\'humain garde le contrôle.',
    pourquoiCritique: 'Sans l\'humain, BZ ne sert à rien — le client irait parler directement à l\'IA. Notre valeur : la couche humaine. Sans innovation cadrée, on stagne. L\'IA est un différenciateur — il faut la maîtriser.',
    concretementBZ: 'Humain garde le contrôle — IA au service des Primes, pas en remplacement. IA dans livrables · LEX/BOT PRIsME · automatisation · roadmap IA · expérimentations 1/mois max · sécurité by design.',
    erreursFrequentes: [
      'IA gadget ou proof-of-concept abandonné',
      'IA sans humain — le client parlerait direct à l\'IA, BZ devient inutile',
      'Trop d\'expérimentations en parallèle',
      'Automatisation sans gouvernance',
      'Stack figé, pas d\'évolution',
      'Innovation non alignée à la création de richesse'
    ],
    niveaux: [
      { niveau: 0, label: 'Statique', description: 'Pas d\'IA, pas d\'automatisation, stack figé' },
      { niveau: 1, label: 'Tests isolés', description: 'Quelques POC IA, expérimentations ponctuelles' },
      { niveau: 2, label: 'Roadmap définie', description: 'Roadmap IA/automatisation, 1-2 projets en cours' },
      { niveau: 3, label: 'Intégrée processus', description: 'IA dans livrables clients, automatisation basique opérationnelle' },
      { niveau: 4, label: 'Culture', description: 'Culture IA, expérimentations régulières cadrées, avantage perçu' },
      { niveau: 5, label: 'Leader', description: 'IA différenciateur reconnu, automatisation mature, stack évolutif' }
    ],
    aFaireMaintenant: [
      'Roadmap IA (livrables, BOT, automatisation)',
      'Pipeline idée → test → standard (1 expérim/mois max)',
      'Standard IA dans Definition of Done',
      'Post-mortem après chaque expérimentation'
    ],
    preuvesVisibles: [
      'Roadmap IA versionnée',
      'Livrables avec IA intégrée',
      'BOT/LEX utilisés',
      'Log décisions innovation'
    ]
  },
  {
    id: 8,
    nom: 'Qualité, KPI & Suivi',
    avatars: ['Eric Durand', 'Guillaume Cloutier', 'Marc-Antoine Caron'],
    sousPiliers: ['Métriques techniques', 'Dashboard pilotage', 'QA systématique', 'Amélioration continue'],
    intention: 'Piloter le département par les données — qualité, délais, adoption, incidents.',
    pourquoiCritique: 'Sans mesure, pas d\'amélioration. Sans KPI actionnables, on navigue à l\'aveugle.',
    concretementBZ: 'Dashboard Prog · KPI qualité (QA pass, rework) · KPI délais (livraisons à temps) · incidents suivis · rituel décision mensuel.',
    erreursFrequentes: [
      'Trop de KPI, aucun actionnable',
      'Données collectées mais non utilisées',
      'QA non systématique',
      'Pas de rituel décision basé sur les chiffres'
    ],
    niveaux: [
      { niveau: 0, label: 'Aucun', description: 'Aucun KPI, qualité non mesurée' },
      { niveau: 1, label: 'Manuel', description: 'Quelques métriques collectées manuellement' },
      { niveau: 2, label: 'Base', description: 'KPI de base définis, QA partielle' },
      { niveau: 3, label: 'Structuré', description: 'Dashboard opérationnel, QA systématique, rituel mensuel' },
      { niveau: 4, label: 'Temps réel', description: 'Métriques temps réel, décisions data-driven' },
      { niveau: 5, label: 'Prédictif', description: 'Amélioration continue, tendances, prédictibilité' }
    ],
    aFaireMaintenant: [
      'Définir 8-10 KPI max (qualité, délais, adoption, incidents)',
      'Dashboard Prog unique (KPI qualité, délais, incidents)',
      'Rituel décision mensuel basé sur les chiffres',
      'Definition of Done avec critères QA mesurables'
    ],
    preuvesVisibles: [
      'Dashboard à jour',
      'Actions correctives prises',
      'Tendances suivies',
      'QA pass rate documenté'
    ]
  }
];

// ============================================================================
// DONNÉES — MATURITÉ ACTUELLE ET CIBLE
// ============================================================================
const maturiteData = [
  { axe: 'Vision & Stratégie', actuel: 2, cible: 3.5 },
  { axe: 'Gouvernance & rôles', actuel: 2, cible: 4 },
  { axe: 'Rentabilité & Rendement', actuel: 2.5, cible: 3.5 },
  { axe: 'RH & Équipe', actuel: 1, cible: 3 },
  { axe: 'Charge projet & Client', actuel: 2.5, cible: 3.5 },
  { axe: 'Technique & Livraison', actuel: 2, cible: 4 },
  { axe: 'Innovation & IA', actuel: 1.5, cible: 3 },
  { axe: 'Qualité, KPI & Suivi', actuel: 1, cible: 2.5 }
];

// ============================================================================
// DONNÉES — RACI (activités groupées + pillier + détail)
// ============================================================================
const raciData = [
  {
    activite: 'Vision et stratégie du département',
    pillier: 'Vision & Stratégie',
    detail: 'Définir la vision annuelle, prioriser les initiatives (backlog stratégique), refuser ce qui n\'aligne pas. Cadre qui guide toutes les décisions du département.',
    roles: { 'Dany Bourassa': 'A', 'Eric Durand': 'R', 'Guillaume Cloutier': 'C', 'Marc-Antoine Caron': 'C' }
  },
  {
    activite: 'Gouvernance comité Prog',
    pillier: 'Gouvernance & rôles',
    detail: 'Documenter les décisions, maintenir le RACI et les processus d\'escalade, animer les rituels (comité Prog). Assure cohérence et traçabilité des décisions.',
    roles: { 'Eric Durand': 'A', 'Guillaume Cloutier': 'R', 'Marc-Antoine Caron': 'C', 'Programmeur': 'I' }
  },
  {
    activite: 'Rentabilité et pilotage financier',
    pillier: 'Rentabilité & Rendement',
    detail: 'Suivi mensuel 150k$/programmeur, marge, charge utile. Dashboard financier et prévisions. Pilotage par les chiffres pour décisions correctives.',
    roles: { 'Eric Durand': 'A', 'Guillaume Cloutier': 'R', 'Programmeur': 'I' }
  },
  {
    activite: 'RH : Onboarding et 1:1',
    pillier: 'RH & Équipe',
    detail: 'Onboarding des programmeurs (PRIsME Employé, checklist par rôle), 1:1 feedback et rétro régulières. Base du développement et de la fidélisation.',
    roles: { 'Eric Durand': 'A', 'Dany Bourassa': 'C', 'Guillaume Cloutier': 'R', 'Marc-Antoine Caron': 'R' }
  },
  {
    activite: 'Parcours carrière et rétention',
    pillier: 'RH & Équipe',
    detail: 'Définir les parcours carrière, mesurer la rétention, identifier les leviers motivationnels. Équipe engagée = livraison durable.',
    roles: { 'Eric Durand': 'A', 'Dany Bourassa': 'C' }
  },
  {
    activite: 'Prise en charge client (owner, PRIsME, suivi)',
    pillier: 'Charge projet & Client',
    detail: 'Owner par client/app (Prime de la prog), PRIsME Client à jour par mandat, suivi client et livraison. Un responsable clair par client — pas d\'orphelin.',
    roles: { 'Guillaume Cloutier': 'A', 'Marc-Antoine Caron': 'R', 'Programmeur': 'R' }
  },
  {
    activite: 'Vente et estimation (devis, démos)',
    pillier: 'Charge projet & Client',
    detail: 'Estimation des mandats et devis techniques, support vente et démos. Interface avec le commercial — chiffrage réaliste et démonstrations convaincantes.',
    roles: { 'Eric Durand': 'A', 'Guillaume Cloutier': 'R', 'Marc-Antoine Caron': 'R', 'Programmeur': 'C' }
  },
  {
    activite: 'Qualité et livraison (DoD, QA, CodeHub, CI/CD)',
    pillier: 'Technique & Livraison',
    detail: 'Definition of Done, QA systématique, pass rate, adoption CodeHub 100%, CI/CD et infra stable. Tout ce qui garantit des livraisons reproductibles et fiables.',
    roles: { 'Eric Durand': 'A', 'Guillaume Cloutier': 'R', 'Marc-Antoine Caron': 'R' }
  },
  {
    activite: 'Sécurité by design',
    pillier: 'Technique & Livraison',
    detail: 'Intégrer la sécurité dès la conception dans les apps. Pas en afterthought — audits, bonnes pratiques, conformité.',
    roles: { 'Guillaume Cloutier': 'A', 'Marc-Antoine Caron': 'R', 'Programmeur': 'R' }
  },
  {
    activite: 'Innovation et IA',
    pillier: 'Innovation & IA',
    detail: 'Roadmap IA et automatisation, expérimentations cadrées (1/mois), standards IA dans les livrables. Créer un avantage concurrentiel sans chaos.',
    roles: { 'Eric Durand': 'A', 'Guillaume Cloutier': 'R', 'Marc-Antoine Caron': 'R', 'Programmeur': 'C' }
  },
  {
    activite: 'KPI et décision basée sur les chiffres',
    pillier: 'Qualité, KPI & Suivi',
    detail: 'Dashboard KPI (qualité, délais, incidents), rituel décision mensuel. Piloter par les données — pas à l\'instinct.',
    roles: { 'Eric Durand': 'A', 'Guillaume Cloutier': 'R', 'Marc-Antoine Caron': 'R', 'Programmeur': 'I' }
  },
  {
    activite: 'Gestion documentaire',
    pillier: 'Technique & Livraison',
    detail: 'Versioning, standards documentaires, accessibilité. Documentation à jour et exploitable.',
    roles: { 'Guillaume Cloutier': 'A', 'Programmeur': 'R', 'Eric Durand': 'C' }
  }
];

// Guidance RACI par activité et lettre — contenu personnalisé par activité (clic sur lettre)
type RaciGuidance = { doit: string[]; nePasFaire: string[]; pieges: string[] };
const raciGuidance: Record<string, Partial<Record<'A' | 'R' | 'C' | 'I', RaciGuidance>>> = {
  'Vision et stratégie du département': {
    A: { doit: ['Valider la vision et les priorités', 'Arbitrer en cas de conflit', 'Refuser ce qui n\'aligne pas'], nePasFaire: ['Micromanager la planification', 'Changer de cap sans communiquer'], pieges: ['Vision floue = exécution sans direction'] },
    R: { doit: ['Proposer vision et priorités', 'Préparer le backlog stratégique', 'Communiquer la vision à l\'équipe'], nePasFaire: ['Exécuter sans alignement', 'Garder la vision pour soi'], pieges: ['R qui décide seul = désalignement'] },
    C: { doit: ['Donner avis sur la faisabilité', 'Challenger les priorités'], nePasFaire: ['Bloquer sans proposer'], pieges: ['Consulté après coup = frustration'] }
  },
  'Gouvernance comité Prog': {
    A: { doit: ['Valider que les décisions sont documentées', 'S\'assurer que RACI et escalade sont à jour'], nePasFaire: ['Laisser des décisions sans trace', 'Utiliser le comité comme causerie'], pieges: ['Décision non documentée = jamais prise'] },
    R: { doit: ['Rédiger CR et décisions', 'Mettre à jour le registre', 'Animer les rituels', 'Diffuser aux concernés'], nePasFaire: ['Résumer en 1 ligne floue', 'Oublier actions et propriétaires'], pieges: ['"On a décidé de..." sans qui fait quoi'] },
    C: { doit: ['Donner avis sur les décisions'], nePasFaire: ['Bloquer sans argument'], pieges: ['C absent = décisions sans contexte'] }
  },
  'Rentabilité et pilotage financier': {
    A: { doit: ['Piloter l\'atteinte 150k$/prog', 'Prendre actions correctives si écart', 'Valider prévisions'], nePasFaire: ['Ignorer les écarts mois après mois', 'Changer d\'objectif sans décision'], pieges: ['Données tardives = décisions en retard'] },
    R: { doit: ['Collecter charge et revenus', 'Produire le dashboard mensuel', 'Préparer l\'analyse pour l\'A'], nePasFaire: ['Inventer des chiffres', 'Oublier des postes de charge'], pieges: ['Charge utile mal définie = métrique fausse'] }
  },
  'RH : Onboarding et 1:1': {
    A: { doit: ['S\'assurer que 1:1 et onboarding ont lieu', 'Donner le cadre du feedback', 'Valider le processus'], nePasFaire: ['Faire les 1:1 soi-même si R en place', 'Ignorer les signaux remontés'], pieges: ['A absent = processus dégradé'] },
    R: { doit: ['Animer 1:1 et onboarding', 'Préparer points concrets', 'Documenter actions et engagements', 'Remonter signaux critiques'], nePasFaire: ['Arriver sans préparation', 'Parler sans écouter', 'Garder blocage majeur pour soi'], pieges: ['1:1 = one-way — doit être un dialogue'] },
    C: { doit: ['Valider le cadre RH global'], nePasFaire: ['Se mêler de l\'exécution'], pieges: ['C qui micro-manage = confusion'] }
  },
  'Parcours carrière et rétention': {
    A: { doit: ['Définir les parcours carrière', 'Valider les leviers rétention', 'Prendre décisions sur rémunération/évolution'], nePasFaire: ['Ignorer la rétention', 'Promesses sans suivi'], pieges: ['Parcours flou = départs évitables'] },
    C: { doit: ['Donner avis sur politiques RH', 'Aligner avec vision BZ'], nePasFaire: ['Décider à la place de l\'A'], pieges: ['C qui impose = désalignement'] }
  },
  'Prise en charge client (owner, PRIsME, suivi)': {
    A: { doit: ['S\'assurer qu\'un owner est identifié par client', 'Valider prise en charge et suivi', 'Escalader si owner dépassé'], nePasFaire: ['Laisser client sans owner', 'Ignorer plaintes client'], pieges: ['Plusieurs owners = personne ne répond'] },
    R: { doit: ['Être point de contact client/app', 'Tenir PRIsME Client à jour', 'Assurer suivi et livraison', 'Signaler risques et blocages'], nePasFaire: ['Abandonner client post-livraison', 'Travailler en silo'], pieges: ['Owner absent = client orphelin'] }
  },
  'Vente et estimation (devis, démos)': {
    A: { doit: ['Valider estimations avant envoi', 'S\'assurer hypothèses documentées', 'Cadrer buffer et marge'], nePasFaire: ['Sous-estimer pour gagner mandat', 'Estimer sans consulter R'], pieges: ['Devis trop bas = pression et qualité'] },
    R: { doit: ['Produire estimation technique', 'Documenter hypothèses et exclusions', 'Participer aux démos'], nePasFaire: ['Inventer des chiffres', 'Oublier dépendances et risques'], pieges: ['Optimisme = dépassement garanti'] },
    C: { doit: ['Donner avis faisabilité', 'Signaler risques techniques'], nePasFaire: ['Bloquer sans alternative'], pieges: ['Consulté trop tard = réestimation'] }
  },
  'Qualité et livraison (DoD, QA, CodeHub, CI/CD)': {
    A: { doit: ['Définir et maintenir la DoD', 'Exiger QA avant prod', 'Valider standards et CodeHub 100%'], nePasFaire: ['Accepter livraisons sans QA', 'Baisser standards sous pression'], pieges: ['DoD floue = chacun interprète'] },
    R: { doit: ['Appliquer DoD sur chaque livrable', 'Faire passer tests et QA', 'Utiliser CodeHub', 'Identifier et corriger écarts'], nePasFaire: ['Livrer sans tests', 'Contourner QA "pour cette fois"'], pieges: ['"On corrige en prod" — jamais acceptable'] }
  },
  'Sécurité by design': {
    A: { doit: ['Valider standards sécurité', 'Exiger audits réguliers', 'Refuser livraison sans revue sécurité'], nePasFaire: ['Ajouter sécurité en afterthought', 'Ignorer vulnérabilités connues'], pieges: ['Sécurité tardive = dette technique'] },
    R: { doit: ['Intégrer sécurité dès la conception', 'Suivre bonnes pratiques', 'Signaler vulnérabilités'], nePasFaire: ['Laisser failles "on corrigera plus tard"', 'Contourner processus sécurité'], pieges: ['Sécurité perçue comme frein = rejet'] }
  },
  'Innovation et IA': {
    A: { doit: ['Valider roadmap IA', 'Cadrer expérimentations (1/mois max)', 'Arbitrer priorités innovation'], nePasFaire: ['Trop d\'initiatives en parallèle', 'Innovation gadget sans ROI'], pieges: ['Chaos = dispersion des efforts'] },
    R: { doit: ['Proposer et exécuter expérimentations', 'Documenter résultats', 'Intégrer standards IA dans livrables'], nePasFaire: ['POC abandonnés', 'Expérimenter sans cadre'], pieges: ['Innovation sans gouvernance = gaspillage'] },
    C: { doit: ['Donner avis faisabilité IA', 'Signaler opportunités'], nePasFaire: ['Bloquer sans proposer'], pieges: ['C absent = idées non challengées'] }
  },
  'KPI et décision basée sur les chiffres': {
    A: { doit: ['Piloter par les KPI', 'Prendre décisions basées sur données', 'Animer le rituel décision mensuel'], nePasFaire: ['Ignorer les tendances', 'Décider à l\'instinct seul'], pieges: ['KPI non utilisés = collecte inutile'] },
    R: { doit: ['Collecter et produire dashboard', 'Préparer analyse pour rituel', 'Proposer actions correctives'], nePasFaire: ['Données incomplètes', 'Présenter sans recommandations'], pieges: ['Dashboard obsolète = perte de crédibilité'] }
  },
  'Gestion documentaire': {
    A: { doit: ['Valider standards documentaires', 'S\'assurer versioning et accessibilité'], nePasFaire: ['Docs obsolètes', 'Pas de propriétaire par section'], pieges: ['Doc dispersée = personne ne trouve'] },
    R: { doit: ['Maintenir docs à jour', 'Appliquer versioning', 'Rendre accessible'], nePasFaire: ['Documenter sans structure', 'Oublier mise à jour'], pieges: ['Doc non maintenue = pire que pas de doc'] },
    C: { doit: ['Donner avis sur structure', 'Signaler manques'], nePasFaire: ['Écrire à la place du R'], pieges: ['Trop de C = doc par comité'] }
  }
};

// Guidance générique si pas de guidance spécifique
const raciGuidanceGeneric: Record<'A' | 'R' | 'C' | 'I', RaciGuidance> = {
  A: { doit: ['Décider et répondre du résultat', 'Valider le travail du R'], nePasFaire: ['Faire le travail à la place du R', 'Micro-manager'], pieges: ['A absent = décisions bloquées'] },
  R: { doit: ['Exécuter le travail', 'Remonter les blocages à l\'A'], nePasFaire: ['Prendre des décisions majeures seul', 'Garder les problèmes pour soi'], pieges: ['R qui décide seul = chaos'] },
  C: { doit: ['Donner son avis quand demandé', 'Apporter expertise'], nePasFaire: ['Bloquer sans argument', 'Se mêler de l\'exécution'], pieges: ['Consulté trop tard = avis inutile'] },
  I: { doit: ['Rester informé'], nePasFaire: ['Exiger d\'être consulté'], pieges: ['I qui bloque = mauvais processus'] }
};

const avatarMap: Record<string, string> = {
  'Dany Bourassa': '/DB.png',
  'Eric Durand': '/ED.png',
  'Marc-Antoine Caron': '/MAC.png',
  'Guillaume Cloutier': '/GC.png',
  'Éric Hains': '/EH.png',
  'Francis Marquis': '/FM.png',
  'Jean-Michel Caouette': '/JMC.png',
  'Jean-Pierre Leclerc': '/JP.png',
  'Pierre-Luc Fortin': '/PLF.png',
  'Gestionnaire': '👤', // Special case - will render as emoji
  'Programmeur': '👤',
  'Comité PRIsME': '/LogoPrismeSmall.png',
  'Primes': '/BZ.png',
  'Directeurs de compte': '$' // Special case - will render as symbol
};

// ============================================================================
// DONNÉES — ROADMAP 2026
// ============================================================================
const normalizeResponsableName = (raw: string): string | null => {
  const value = raw.trim().replace(/^[-–—]\s*/, '');
  if (!value) return null;

  const lowered = value.toLowerCase();
  if (['team', 'team prog', 'équipe', 'equipe'].includes(lowered)) return 'Programmeur';
  if (lowered === 'marco') return 'Marc-Antoine Caron';
  if (lowered === 'éric' || lowered === 'eric') return 'Eric Durand';
  if (lowered === 'guillaume') return 'Guillaume Cloutier';
  if (lowered === 'dany') return 'Dany Bourassa';

  return value;
};

const splitLivrableTextAndResponsables = (texte: string): { texte: string; responsables: string[] } => {
  if (!texte.includes('—')) return { texte: texte.trim(), responsables: [] };

  const parts = texte.split('—').map(part => part.trim()).filter(Boolean);
  if (parts.length < 2) return { texte: texte.trim(), responsables: [] };

  const rawResponsables = parts[parts.length - 1]
    .replace(/\((?:en cours|cette semaine)\)/gi, '')
    .split(/\s*(?:,| et | ou |\/ )\s*/i)
    .map(normalizeResponsableName)
    .filter((value): value is string => Boolean(value));

  if (rawResponsables.length === 0) return { texte: texte.trim(), responsables: [] };

  return {
    texte: parts.slice(0, -1).join(' — ').trim(),
    responsables: Array.from(new Set(rawResponsables))
  };
};

const roadmap2026 = [
  {
    trimestre: 'Q2 2026',
    intention: 'Structurer le département, clarifier la gouvernance, lancer les bases IA et solidifier les pratiques de suivi.',
    axes: [
      {
        axe: 'Rentabilité et rendement',
        intention: 'Créer les fondations de rentabilité, de communication et de responsabilité par application.',
        livrables: [
          { texte: 'Plan marketing à faire et présenter — Éric et Guillaume', effort: 16, priorite: 'Haute' },
          { texte: 'Définir la prise en charge des app / jalon, communication avec Prog — Guillaume', effort: 16, priorite: 'Haute' },
          { texte: 'Compléter la doc RACI par app — Guillaume', effort: 30, priorite: 'Haute' }
        ]
      },
      {
        axe: 'RH et équipe',
        intention: 'Préparer l’onboarding et les bases d’intégration du département avec l’administration.',
        livrables: [
          { texte: 'Prévoir la méthode d’onboarding avec admin — Éric', effort: 2, priorite: 'Moyenne' }
        ]
      },
      {
        axe: 'Charge Projet et Client',
        intention: 'Mieux suivre les plans correcteurs et préparer le support IA aux suivis projet/client.',
        livrables: [
          { texte: 'Suivi au trois mois des avancements des plans correcteurs — Guillaume', effort: 8, priorite: 'Haute' },
          { texte: 'Construire un agent pour suivi et résumé des tâche et jalon lié au plan directeur', effort: 8, priorite: 'Haute' }
        ]
      },
      {
        axe: 'Technique, Livraison et Innovation IA',
        intention: 'Lancer les chantiers structurants IA, sécurité et outils d’exécution du département.',
        livrables: [
          { texte: 'création de cursor rules standardisé touche à security — Guillaume (cette semaine)', effort: 2, priorite: 'Haute' },
          { texte: 'Agent IA dans application interne (ex. Codehub) — team', effort: 24, priorite: 'Haute' },
          { texte: 'Développement des skills pour construire et encadré les agents IA — team prog (à déterminer)', effort: 0, priorite: 'Moyenne' },
          { texte: 'Créer plan directeur PROG IA — Guillaume', effort: 1, priorite: 'Haute' },
          { texte: 'Intégration d’un LLM local BZ, plan de développement — Marco', effort: 24, priorite: 'Haute' }
        ]
      },
      {
        axe: 'Qualité KPI et suivi',
        intention: 'Mettre en place les premiers rituels de suivi, visibilité et feedback.',
        livrables: [
          { texte: 'Écran télé avec dashboard Prog — Éric et Guillaume', effort: 4, priorite: 'Moyenne' },
          { texte: 'Implantation d’un rituel mensuel scrum plus gros par mois — Guillaume et Éric (en cours)', effort: 0, priorite: 'Moyenne' },
          { texte: 'Implantation meeting mensuel comité prog — Guillaume', effort: 1, priorite: 'Moyenne' },
          { texte: 'Sondage de satisfaction fait au client électronique (app et responsable), forms — Guillaume', effort: 1, priorite: 'Moyenne' }
        ]
      },
      {
        axe: 'Vision et stratégique',
        intention: 'Définir les priorités stratégiques non négociables du département.',
        livrables: [
          { texte: 'Définir trois priorité stratégique non négociable (à mettre dans le 2e meeting officiel)', effort: 0, priorite: 'Haute' }
        ]
      }
    ]
  },
  {
    trimestre: 'Q3 2026',
    intention: 'Faire évoluer les pratiques vers des standards clairs, des outils plus structurés et une meilleure capacité de livraison IA.',
    axes: [
      {
        axe: 'Rentabilité et rendement',
        intention: 'Mesurer la performance et la rentabilité par client.',
        livrables: [
          { texte: 'Rentabilité et rendement — tableau de bord et rendement par client — Éric', effort: 16, priorite: 'Haute' }
        ]
      },
      {
        axe: 'RH et équipe',
        intention: 'Formaliser l’onboarding et faire progresser PRIsME Employé dans le département.',
        livrables: [
          { texte: 'Définir onboarding clair sous forme de document officiel (checklist) — Marco', effort: 4, priorite: 'Haute' },
          { texte: 'Configuration des PRIsME Employé (75%) — Éric', effort: 40, priorite: 'Haute' }
        ]
      },
      {
        axe: 'Charge Projet et Client',
        intention: 'Structurer la documentation, les cahiers de charge et le suivi applicatif.',
        livrables: [
          { texte: 'Documentation des prise en charge des app et suivi : construction des cahiers de charge et jalons avec transcription — Guillaume', effort: 60, priorite: 'Haute' }
        ]
      },
      {
        axe: 'Technique, Livraison et Innovation IA',
        intention: 'Définir les standards IA, encadrer le QA et commencer l’intégration client de confiance.',
        livrables: [
          { texte: 'Définition du code et QA (agent dans Git) — Marco', effort: 8, priorite: 'Haute' },
          { texte: 'Agents IA dans les app de quelques client de confiance, cadre en développement', effort: 60, priorite: 'Haute' },
          { texte: 'Standard IA et definition of done (75%) — Marco', effort: 2, priorite: 'Haute' },
          { texte: 'Mesuré et évalué (KPI) délai de livraison estimé — Éric ou Guillaume', effort: 4, priorite: 'Moyenne' },
          { texte: 'Chaque projet IA, post-mortem procédural nécessaire (rencontre documentable) (1h / post-mortem)', effort: 0, priorite: 'Moyenne' },
          { texte: 'Intégration d’un LLM local BZ, Amélioration — Marco', effort: 24, priorite: 'Haute' }
        ]
      },
      {
        axe: 'Vision et stratégique',
        intention: 'Partager une vision commune et compréhensible du département Programmation & IA.',
        livrables: [
          { texte: 'Définir la vision de prog pour tout le monde — Guillaume ou Éric', effort: 2, priorite: 'Haute' }
        ]
      }
    ]
  },
  {
    trimestre: 'Q4 2026',
    intention: 'Finaliser les déploiements structurants, compléter les standards et faire converger le département vers un modèle plus mature et scalable.',
    axes: [
      {
        axe: 'RH et équipe',
        intention: 'Compléter PRIsME Employé et finaliser l’intégration du département.',
        livrables: [
          { texte: 'Configuration des PRIsME Employé (100%) — Éric', effort: 40, priorite: 'Haute' }
        ]
      },
      {
        axe: 'Charge Projet et Client',
        intention: 'Faire converger la structure client vers une couverture plus complète.',
        livrables: [
          { texte: 'Avoir un prisme client pour chaque client — team (8h par/client)', effort: 0, priorite: 'Haute' }
        ]
      },
      {
        axe: 'Technique, Livraison et Innovation IA',
        intention: 'Passer des initiatives pilotes à une standardisation plus large.',
        livrables: [
          { texte: 'Agents IA dans tout nos apps (standardisé)', effort: 0, priorite: 'Haute' },
          { texte: 'Standard IA et definition of done (100%) — Marco', effort: 2, priorite: 'Haute' }
        ]
      },
      {
        axe: 'Gouvernance et rôle',
        intention: 'Finaliser les mécanismes de gouvernance nécessaires à la maturité du département.',
        livrables: [
          { texte: 'Processus d’escalade — Guillaume ou Éric', effort: 2, priorite: 'Moyenne' }
        ]
      }
    ]
  }
];

type RoadmapOperationnelAxeId =
  | 'PRISME_CLIENT_LIVRAISON'
  | 'CONTENU_METHODE'
  | 'VENTE_COMMUNICATION'
  | 'ADOPTION_FORMATION'
  | 'GOUVERNANCE_QUALITE'
  | 'IA_BOT'
  | 'GOUVERNANCE_SUIVI'
  | 'PRISME_CLIENT_MONTEE'
  | 'PRISME_EMPLOYE_METHODE'
  | 'ADOPTION_EXPERIENCE_CLIENT'
  | 'VENTE_POSITIONNEMENT'
  | 'INTERNE_FORMATION'
  | 'IA_BOT_SPIDER'
  | 'QUALITE_CERTIFICATION'
  | 'GOUVERNANCE_STRATEGIE'
  | 'VENTE_IMAGE';

type RoadmapOperationnelTask = {
  tache: string;
  r: string;
  a: string;
  heures: number;
  jalon: string;
  critere: string;
  // Avancement (0-100) du jalon associé à cette tâche (PMO).
  // Persisté en base de données (partagé).
  avancementPct?: number;
};

type RoadmapOperationnelAxe = {
  id: RoadmapOperationnelAxeId;
  nom: string;
  tasks: RoadmapOperationnelTask[];
};

type RoadmapOperationnelQuarter = {
  trimestre: 'Q1 2026' | 'Q2 2026' | 'Q3 2026' | 'Q4 2026';
  theme: string;
  axes: RoadmapOperationnelAxe[];
  totalHeures?: number;
  synthese?: string[];
};

// ============================================================================
// DONNÉES — ROADMAP OPÉRATIONNELLE 2026 (EXÉCUTION)
// Basée sur la version préparée par l’équipe (PMO).
// ============================================================================
const roadmapOperationnel2026: RoadmapOperationnelQuarter[] = [
  {
    trimestre: 'Q1 2026',
    theme: 'Fondations & mise en cadence',
    axes: [
      {
        id: 'PRISME_CLIENT_LIVRAISON',
        nom: 'Axe 1 — PRISME Client & livraison',
        tasks: [
          { tache: '15 → 50 clients PRISME', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 60, jalon: '50 clients actifs', critere: 'Clients visibles + comité actif' },
          { tache: 'R2 / documents (20 clients)', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 45, jalon: '20 clients outillés', critere: 'Docs utilisés en comité' },
          { tache: 'Rodage PRISME client', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 30, jalon: 'Process rodé', critere: '< 20 % reprises' },
          { tache: "Catalogue d'actifs PRISME", r: 'Jean-Michel', a: 'Éric', heures: 25, jalon: 'Catalogue actif', critere: 'API dans PRISME' }
        ]
      },
      {
        id: 'CONTENU_METHODE',
        nom: 'Axe 2 — Contenu & méthode PRISME',
        tasks: [
          { tache: 'TACTICE intégré', r: 'Dany / Jean-Michel', a: 'Éric', heures: 30, jalon: 'TACTICE live', critere: 'Onglet Projet créé et 90% fonctionnel' },
          { tache: '10 ODS livrés', r: 'Pierre-Luc', a: 'Francis', heures: 35, jalon: '10 ODS', critere: 'Utilisés en comité' },
          { tache: 'PRISME Employé V1 (60 %)', r: 'Jean-Pierre', a: 'Jean-Pierre', heures: 40, jalon: 'V1 livrée', critere: 'Accès OK' },
          { tache: 'PRISME Département V1', r: 'Jean-Michel', a: 'Dany', heures: 25, jalon: 'V1 livrée', critere: 'Structure validée' }
        ]
      },
      {
        id: 'VENTE_COMMUNICATION',
        nom: 'Axe 3 — Vente & communication',
        tasks: [
          { tache: '10 ventes PRISME', r: 'Pierre-Luc', a: 'Francis', heures: 35, jalon: '10 ventes', critere: 'Opportunités fermées' },
          { tache: 'Adoption ODS vente (50 %)', r: 'Pierre-Luc', a: 'Francis', heures: 20, jalon: '50 % équipe', critere: 'ODS utilisés' },
          { tache: 'Plan communication PRISME', r: 'Pierre-Luc', a: 'Francis', heures: 15, jalon: 'Plan validé', critere: 'Plan appliqué' },
          { tache: '1re communication PRISME', r: 'Pierre-Luc', a: 'Francis', heures: 10, jalon: 'Communication faite', critere: 'Présentation utilisée' }
        ]
      },
      {
        id: 'ADOPTION_FORMATION',
        nom: 'Axe 4 — Adoption interne & formation',
        tasks: [
          { tache: 'Présentation PRISME interne', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 10, jalon: 'Présentation faite', critere: 'Feedback reçu' },
          { tache: 'Formation interne PRISME', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 15, jalon: 'Formation livrée', critere: 'Équipe formée' },
          { tache: 'Adoption interne PRISME', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 20, jalon: 'Adoption enclenchée', critere: 'Usage réel' }
        ]
      },
      {
        id: 'GOUVERNANCE_QUALITE',
        nom: 'Axe 5 — Gouvernance & qualité',
        tasks: [
          { tache: 'Cadence comités PRISME', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 15, jalon: 'Rituels en place', critere: 'Comités tenus' },
          { tache: 'Qualité documents (rodage)', r: 'Pierre-Luc', a: 'Jean-Pierre', heures: 15, jalon: 'Critères définis', critere: 'Check qualité' },
          { tache: 'Suivi avancement Q1', r: 'Pierre-Luc', a: 'Jean-Pierre', heures: 10, jalon: 'Tableau suivi', critere: 'Avancement mesuré' }
        ]
      }
    ],
    totalHeures: 470,
    synthese: ['Fondations solides et crédibilité', 'PRISME fonctionne pour vrai']
  },
  {
    trimestre: 'Q2 2026',
    theme: 'Industrialisation & stabilisation',
    axes: [
      {
        id: 'PRISME_CLIENT_MONTEE',
        nom: 'Axe 1 — PRISME Client & montée en charge',
        tasks: [
          { tache: '50 → 80 clients PRISME', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 70, jalon: '80 clients actifs', critere: 'Clients visibles' },
          { tache: 'Process client stabilisé', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 40, jalon: 'Stabilité atteinte', critere: '< 10 % reprises' },
          { tache: '65 documents livrés', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 35, jalon: '65 docs', critere: 'Docs utilisés' }
        ]
      },
      {
        id: 'PRISME_EMPLOYE_METHODE',
        nom: 'Axe 2 — PRISME Employé & méthode',
        tasks: [
          { tache: 'PRISME Employé complété (100 %)', r: 'Gestionnaires', a: 'Jean-Pierre', heures: 45, jalon: 'Complet', critere: 'Contenu accessible' },
          { tache: 'Adoption PRISME Employé (80 %)', r: 'Gestionnaires', a: 'Jean-Pierre', heures: 30, jalon: '80 % employés', critere: 'Usage réel' },
          { tache: 'Gestion droits & accès', r: 'Gestionnaires', a: 'Jean-Pierre', heures: 20, jalon: 'Accès gérés', critere: 'Sécurisé' }
        ]
      },
      {
        id: 'IA_BOT',
        nom: 'Axe 3 — IA & BOT',
        tasks: [
          { tache: 'BOT PRISME V1', r: 'Jean-Michel', a: 'Dany', heures: 30, jalon: 'BOT live', critere: 'Réponses fonctionnelles' },
          { tache: 'Suivi projet IA', r: 'Jean-Michel', a: 'Éric', heures: 20, jalon: 'Suivi actif', critere: 'Avancement documenté' },
          { tache: 'Qualité réponses BOT', r: 'Pierre-Luc', a: 'Jean-Pierre', heures: 15, jalon: 'Qualité mesurée', critere: '> 70 % utiles' }
        ]
      },
      {
        id: 'VENTE_COMMUNICATION',
        nom: 'Axe 4 — Vente & communication',
        tasks: [
          { tache: 'Cadre vente PRISME unifié', r: 'Pierre-Luc', a: 'Francis', heures: 25, jalon: 'Discours unifié', critere: 'Utilisé vente' },
          { tache: 'Formation vente PRISME', r: 'Pierre-Luc', a: 'Francis', heures: 20, jalon: 'Formation livrée', critere: 'Équipe formée' },
          { tache: 'Onglet réunion PRISME amélioré', r: 'Pierre-Luc', a: 'Francis', heures: 15, jalon: 'Onglet prêt', critere: 'Utilisation terrain' }
        ]
      },
      {
        id: 'GOUVERNANCE_SUIVI',
        nom: 'Axe 5 — Gouvernance & suivi',
        tasks: [
          { tache: 'KPI PRISME définis', r: 'Pierre-Luc', a: 'Jean-Pierre', heures: 20, jalon: 'KPI validés', critere: 'Utilisés comité' },
          { tache: 'Suivi adoption & performance', r: 'Pierre-Luc', a: 'Jean-Pierre', heures: 15, jalon: 'Tableau actif', critere: 'Données à jour' },
          { tache: 'Cadence comités stabilisée', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 15, jalon: 'Cadence stable', critere: 'Sans friction' }
        ]
      }
    ],
    totalHeures: 470,
    synthese: ['PRISME industrialisé et stable', 'Moins de dépendance au hero mode']
  },
  {
    trimestre: 'Q3 2026',
    theme: 'Maturité & création de valeur',
    axes: [
      {
        id: 'PRISME_CLIENT_MONTEE',
        nom: 'Axe 1 — PRISME Client & maturité',
        tasks: [
          { tache: '80 → 120 clients PRISME', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 80, jalon: '120 clients actifs', critere: 'Clients visibles' },
          { tache: '+100 documents livrés', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 50, jalon: '+100 docs', critere: 'Utilisés en comité' },
          { tache: '100 % ODS via PRISME', r: 'Pierre-Luc', a: 'Francis', heures: 30, jalon: 'Tous ODS', critere: 'Aucun hors PRISME' }
        ]
      },
      {
        id: 'ADOPTION_EXPERIENCE_CLIENT',
        nom: 'Axe 2 — Adoption & expérience client',
        tasks: [
          { tache: 'Adoption SPIDER (30 % clients)', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 35, jalon: '30 % clients', critere: 'Utilisé en comité' },
          { tache: 'Qualité comités TI (90 %)', r: 'Pierre-Luc', a: 'Jean-Pierre', heures: 20, jalon: '90 % efficaces', critere: 'Feedback positif' },
          { tache: 'Adoption cadre BZ (30 %)', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 30, jalon: '30 % clients', critere: 'Cadre utilisé' }
        ]
      },
      {
        id: 'IA_BOT',
        nom: 'Axe 3 — IA & innovation',
        tasks: [
          { tache: 'Qualité BOT IA (90 %)', r: 'Pierre-Luc', a: 'Jean-Pierre', heures: 25, jalon: '90 % utiles', critere: 'Mesure qualité' },
          { tache: 'Adoption BOT (50 %)', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 30, jalon: '50 % employés', critere: 'Usage réel' },
          { tache: 'Optimisation BOT', r: 'Jean-Michel', a: 'Éric', heures: 20, jalon: 'BOT optimisé', critere: 'Moins erreurs' }
        ]
      },
      {
        id: 'VENTE_POSITIONNEMENT',
        nom: 'Axe 4 — Vente & positionnement',
        tasks: [
          { tache: 'Commercialisation PRISME V1', r: 'Pierre-Luc', a: 'Francis', heures: 30, jalon: 'Offre prête', critere: 'Utilisée vente' },
          { tache: 'Packaging grands comptes', r: 'Pierre-Luc', a: 'Francis', heures: 20, jalon: 'Packaging prêt', critere: 'Terrain' },
          { tache: 'Plan communication complet', r: 'Pierre-Luc', a: 'Francis', heures: 15, jalon: '100 % actions', critere: 'Réalisées' }
        ]
      },
      {
        id: 'INTERNE_FORMATION',
        nom: 'Axe 5 — Interne & formation',
        tasks: [
          { tache: 'Formation interne PRISME rodée', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 20, jalon: 'Formation maîtrisée', critere: 'Autonomie' },
          { tache: '+30 documents internes', r: 'Pierre-Luc', a: 'Francis', heures: 25, jalon: '+30 docs', critere: 'Utilisés' },
          { tache: 'Gestion documentaire PRISME', r: 'Jean-Michel', a: 'Francis', heures: 20, jalon: 'Fonctionnelle', critere: 'Docs gérés' }
        ]
      }
    ],
    totalHeures: 505,
    synthese: ['PRISME prouve sa valeur', 'Qualité et adoption élevées']
  },
  {
    trimestre: 'Q4 2026',
    theme: 'Pérennité & projection 2027',
    axes: [
      {
        id: 'PRISME_CLIENT_MONTEE',
        nom: 'Axe 1 — PRISME Client & objectifs annuels',
        tasks: [
          { tache: '120 → 150 clients PRISME', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 70, jalon: '150 clients actifs', critere: 'Clients + comités actifs' },
          { tache: '+150 documents livrés', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 55, jalon: '150 docs', critere: 'Utilisés' },
          { tache: 'Adoption cadre BZ (50 %)', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 35, jalon: '50 % clients', critere: 'Cadre actif' }
        ]
      },
      {
        id: 'IA_BOT_SPIDER',
        nom: 'Axe 2 — IA, BOT & SPIDER',
        tasks: [
          { tache: 'Adoption BOT interne (75 %)', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 30, jalon: '75 % employés', critere: 'Usage mesuré' },
          { tache: 'SPIDER complété (100 %)', r: 'Francis / Pierre-Luc / Jean-Michel', a: 'Éric', heures: 25, jalon: 'Skill complet', critere: 'Utilisation réelle' },
          { tache: 'Qualité BOT > 95 %', r: 'Pierre-Luc', a: 'Jean-Pierre', heures: 20, jalon: '95 % utiles', critere: 'Mesure qualité' }
        ]
      },
      {
        id: 'QUALITE_CERTIFICATION',
        nom: 'Axe 3 — Qualité & certification',
        tasks: [
          { tache: 'Plan certification PRISME', r: 'Pierre-Luc / Jean-Michel', a: 'Jean-Pierre', heures: 30, jalon: 'Plan prêt', critere: 'Critères définis' },
          { tache: 'Qualité PRISME 90 %', r: 'Pierre-Luc', a: 'Jean-Pierre', heures: 25, jalon: 'Audit OK', critere: 'Conforme' },
          { tache: 'Adoption PRISME Employé (75 %)', r: 'Gestionnaires', a: 'Jean-Pierre', heures: 25, jalon: '75 % employés', critere: 'Usage réel' }
        ]
      },
      {
        id: 'GOUVERNANCE_STRATEGIE',
        nom: 'Axe 4 — Gouvernance & stratégie',
        tasks: [
          { tache: 'Bilan roadmap 2026', r: 'Éric', a: 'Dany', heures: 20, jalon: 'Bilan produit', critere: 'Écarts documentés' },
          { tache: 'Roadmap PRISME 2027', r: 'Éric', a: 'Dany', heures: 30, jalon: 'Validée', critere: 'Go comité' },
          { tache: 'Gouvernance pérennité', r: 'Éric', a: 'Dany', heures: 15, jalon: 'Stable', critere: 'Aucun point bloquant' }
        ]
      },
      {
        id: 'VENTE_IMAGE',
        nom: 'Axe 5 — Vente & image',
        tasks: [
          { tache: 'Objectifs vente atteints', r: 'Pierre-Luc', a: 'Francis', heures: 25, jalon: 'Objectifs OK', critere: 'Résultats mesurés' },
          { tache: 'Image PRISME haut de gamme', r: 'Pierre-Luc', a: 'Francis', heures: 15, jalon: 'Image consolidée', critere: 'Feedback marché' },
          { tache: 'Onboarding vente 100 %', r: 'Pierre-Luc', a: 'Francis', heures: 10, jalon: 'Équipe prête', critere: 'Autonomie ventes' }
        ]
      }
    ],
    totalHeures: 465,
    synthese: ['PRISME pérenne et crédible', 'Aucune dette 2026', 'Projection 2027 prête']
  }
];

export default function GouvernanceProgrammationIAModal({ isOpen, onClose }: GouvernanceProgrammationIAModalProps) {
  const [activeTab, setActiveTab] = useState<'esprit' | 'cadre' | 'maturite' | 'raci' | 'roadmap' | 'rituels'>('esprit');
  const { dates: lexDates } = useLexLastSync();
  const lastAnalysisDate = isOpen ? (lexDates['gouvernance_prisme_last_ai_analysis'] ?? null) : null;

  const tabs = [
    { id: 'esprit' as const, label: 'Esprit & Objectifs macro', icon: Eye },
    { id: 'cadre' as const, label: 'Cadre holistique', icon: BookOpen },
    { id: 'maturite' as const, label: 'Maturité (Spider + Bar)', icon: Target },
    { id: 'raci' as const, label: 'Rôles & responsabilités', icon: Users },
    { id: 'roadmap' as const, label: 'Roadmap 2026', icon: Calendar },
    { id: 'rituels' as const, label: 'Gouvernance & rituels', icon: Shield },
  ];

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-br from-slate-900 via-indigo-900 to-slate-900 rounded-2xl w-[90vw] h-[90vh] border border-indigo-500/30 shadow-2xl shadow-indigo-500/20 flex flex-col">
              {/* Header */}
              <div className="relative p-3 border-b border-indigo-500/30 bg-gradient-to-r from-indigo-900/50 to-purple-900/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg">
                      <Shield className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">
                        Gouvernance Programmation & IA
                      </h2>
                      <p className="text-gray-400 text-xs">
                        Cadre holistique de maturité — Département qui rend possible l&apos;excellence
                      </p>
                      <div className="flex items-center gap-3 mt-2 flex-wrap">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-purple-500/30 border border-purple-400/50 text-xs text-purple-200" title="Disponible dans AI Assistant">
                          <Brain className="w-3.5 h-3.5" />
                          AI
                        </span>
                        {lastAnalysisDate && (
                          <span className="text-gray-400 text-xs flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5" />
                            Dernière synchro LEX: {formatLastAnalysisDate(lastAnalysisDate)}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Avatars participants clés — Eric Durand (A), Marc-Antoine, Guillaume, Dany */}
                    <div className="flex items-center gap-2">
                      <img src="/ED.png" alt="Eric Durand" className="w-8 h-8 rounded-full border-2 border-indigo-400 ring-2 ring-emerald-500/50" title="Eric Durand — Accountable, Directeur département" />
                      <img src="/MAC.png" alt="Marc-Antoine Caron" className="w-8 h-8 rounded-full border-2 border-indigo-400" title="Marc-Antoine Caron" />
                      <img src="/GC.png" alt="Guillaume Cloutier" className="w-8 h-8 rounded-full border-2 border-indigo-400" title="Guillaume Cloutier" />
                      <img src="/DB.png" alt="Dany Bourassa" className="w-8 h-8 rounded-full border-2 border-indigo-400" title="Dany Bourassa" />
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-1.5 hover:bg-white/10 rounded-lg transition-colors ml-2"
                  >
                    <X className="w-5 h-5 text-gray-400" />
                  </button>
                </div>
              </div>

              {/* Tabs Navigation */}
              <div className="border-b border-indigo-500/30 bg-slate-900/50 px-6 pt-3 pb-0 overflow-x-auto">
                <div className="flex gap-2">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-semibold text-sm transition-all whitespace-nowrap ${
                          activeTab === tab.id
                            ? 'bg-gradient-to-b from-indigo-600 to-purple-600 text-white shadow-lg'
                            : 'text-gray-400 hover:text-white hover:bg-white/5'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {tab.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                <AnimatePresence mode="wait">
                  {activeTab === 'esprit' && <EspritTab />}
                  {activeTab === 'cadre' && <CadreTab />}
                  {activeTab === 'maturite' && <MaturiteTab />}
                  {activeTab === 'raci' && <RACITab />}
                  {activeTab === 'roadmap' && <RoadmapTab />}
                  {activeTab === 'rituels' && <RituelsTab />}
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

// ============================================================================
// ONGLET 1 — ESPRIT & OBJECTIFS MACRO (Programmation & IA)
// ============================================================================
function EspritTab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-indigo-600/20 rounded-xl p-6 border-2 border-indigo-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-2">
          Esprit & Objectifs macro
        </h3>
        <p className="text-gray-300 text-center text-sm">
          Version exécutive : pourquoi le département existe, ce qu&apos;il règle, ce qu&apos;il refuse et ce qu&apos;on veut voir à la fin.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="bg-white/5 rounded-xl p-5 border border-indigo-500/20">
          <h4 className="text-lg font-bold text-white mb-3">À retenir en 30 secondes</h4>
          <ul className="space-y-2 text-sm text-gray-300">
            <li>• Le département existe pour transformer des idées, données et processus en valeur concrète.</li>
            <li>• Il doit livrer vite, bien, avec une gouvernance claire et une IA utile.</li>
            <li>• Son rôle est de rendre possible ce qui crée un vrai avantage B2B pour BZ et ses clients.</li>
            <li>• Il doit aussi bâtir un plan Marketing WEB clair pour faire rayonner l’offre Programmation & IA avec un angle ROI client.</li>
          </ul>
        </div>
        <div className="bg-white/5 rounded-xl p-5 border border-emerald-500/20">
          <h4 className="text-lg font-bold text-white mb-3">Ce qu&apos;on veut voir</h4>
          <ul className="space-y-2 text-sm text-gray-300">
            <li>• Des standards clairs.</li>
            <li>• Une qualité constante.</li>
            <li>• Une équipe plus rapide, plus alignée et plus rentable.</li>
            <li>• Une IA intégrée sans chaos ni complexité inutile.</li>
            <li>• Un Marketing WEB capable de soutenir la traction commerciale sans dépendre seulement des ventes.</li>
          </ul>
        </div>
      </div>

      {/* Pourquoi la gouvernance — boussole et raison d'être */}
      <div className="bg-gradient-to-br from-indigo-600/30 via-purple-600/20 to-indigo-600/30 rounded-xl p-6 border-2 border-indigo-500/40">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Shield className="w-6 h-6 text-indigo-400" />
          Pourquoi la gouvernance — notre boussole
        </h4>
        <p className="text-gray-300 leading-relaxed mb-4">
          La gouvernance n&apos;est pas de la paperasse. C&apos;est la <strong className="text-white">boussole du département</strong> — elle définit le cap, les règles du jeu et les limites. Sans elle, on exécute à l&apos;aveugle. Avec elle, chaque décision s&apos;inscrit dans un cadre clair.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          Aujourd&apos;hui, avec l&apos;IA qui accélère tout — idées, prototypes, livraisons — la gouvernance est <strong className="text-indigo-300">plus critique que jamais</strong>. L&apos;IA multiplie les possibilités, mais sans cadre défini, elle amplifie le chaos. On risque de livrer vite, mais mal. D&apos;innover sans alignement. De diverger sans s&apos;en rendre compte.
        </p>
        <p className="text-white font-semibold leading-relaxed">
          La règle d&apos;or : <span className="text-indigo-300">définir les cadres avant l&apos;exécution</span>. Rôles, standards, processus, critères de qualité — tout cela doit être posé en amont. Pas improvisé en cours de route. C&apos;est ce qui permet à l&apos;IA et à l&apos;équipe de déployer leur plein potentiel, de façon cohérente et durable.
        </p>
      </div>

      {/* Vision — hybride punch + stratégique */}
      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Eye className="w-6 h-6 text-indigo-400" />
          Vision
        </h4>
        <p className="text-white text-xl font-bold mb-4">
          On crée de la richesse durable. BZ ou externe. Point.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          En transformant leurs idées, leurs données et leurs processus en <strong className="text-white">leviers concrets de performance</strong>. C&apos;est nous qui rendons possible ce qui semblait complexe, lent ou inaccessible.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          Dans un monde où l&apos;IA redéfinit les règles, on permet aux organisations de structurer, automatiser et maîtriser leurs opérations avec rigueur et intelligence. <span className="text-indigo-300 font-semibold">On ne subit pas. On construit.</span>
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          Documentation, méthodologie, automatisation, formation, analyse, contrôle qualité — chaque intervention vise un objectif clair : <strong className="text-white">faire gagner du temps</strong>, <strong className="text-white">réduire les frictions</strong>, <strong className="text-white">viser l&apos;excellence avec l&apos;IA</strong>, <strong className="text-white">créer un avantage concurrentiel réel en B2B</strong>. On se doit à l&apos;excellence. Nos clients se démarquent. Par nos réalisations.
        </p>
        <p className="text-indigo-300 font-bold text-lg mb-4">
          Des solutions utiles. Adoptées rapidement. ROI mesurable, rapide et durable. Pas négociable.
        </p>
        <p className="text-gray-300 italic border-l-2 border-indigo-500/50 pl-4">
          L&apos;excellence se livre. Mais l&apos;humain reste l&apos;ingrédient clé — celui qui donne vie au virtuel, qui transforme l&apos;outil en levier.
        </p>
      </div>

      {/* Pourquoi le département existe */}
      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Target className="w-6 h-6 text-indigo-400" />
          Pourquoi le département existe
        </h4>
        <p className="text-gray-300 leading-relaxed mb-4">
          Parce que les organisations ont des idées, des données, des processus — mais peu ont la capacité de les transformer en richesse réelle. C&apos;est notre raison d&apos;être.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          Parce que l&apos;IA multiplie les possibles, mais sans rigueur — méthodologie, documentation, formation, contrôle qualité — elle reste un gadget. On est le lien entre le potentiel et la livraison.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          Parce qu&apos;en B2B, le temps presse. On ne livre pas pour impressionner. On livre pour prouver : ROI rapide et durable.
        </p>
        <p className="text-gray-300 leading-relaxed">
          Et parce qu&apos;une bonne équipe de programmation & IA ne doit pas seulement livrer : elle doit aussi savoir structurer un Marketing WEB clair pour faire comprendre sa valeur, générer de la traction et soutenir les ventes avec un discours ROI client.
        </p>
      </div>

      {/* Ce que le département règle */}
      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <CheckCircle className="w-6 h-6 text-emerald-400" />
          Ce que le département règle
        </h4>
        <div className="space-y-4">
          <div>
            <p className="text-white font-semibold mb-1">Le chaos opérationnel</p>
            <p className="text-gray-400 text-sm">En concevant des outils sur mesure (ERP, CRM, automatisation, gestion de tâches, projets, rapports, finances, RH) qui reflètent la vraie façon de travailler, pas l&apos;inverse.</p>
          </div>
          <div>
            <p className="text-white font-semibold mb-1">Tout dans la tête, rien d&apos;écrit</p>
            <p className="text-gray-400 text-sm">Une doc qui sert. Qui vit. Pas des PDFs oubliés — un actif qui crée de la valeur.</p>
          </div>
          <div>
            <p className="text-white font-semibold mb-1">Des standards clairs. Des résultats constants.</p>
            <p className="text-gray-400 text-sm">Une méthodologie claire. Les bonnes pratiques deviennent des standards. Tout le monde peut livrer pareil.</p>
          </div>
          <div>
            <p className="text-white font-semibold mb-1">Repousser les limites par l&apos;automatisation et l&apos;IA</p>
            <p className="text-gray-400 text-sm">En utilisant l&apos;automatisation et l&apos;IA pour rendre possible ce qui était lent, coûteux ou irréalisable.</p>
          </div>
          <div>
            <p className="text-white font-semibold mb-1">L&apos;humain avant la solution ⭐</p>
            <p className="text-gray-400 text-sm">Notre valeur commence par l&apos;écoute, pas par la technologie. Formation et analyse pour les outiller — mais c&apos;est leur jugement qu&apos;on valorise. On les comprend, on les accompagne. Les aider à trancher, mieux et plus vite.</p>
          </div>
          <div>
            <p className="text-white font-semibold mb-1">Le vide marketing autour d&apos;une bonne offre</p>
            <p className="text-gray-400 text-sm">Une équipe forte peut livrer d&apos;excellentes solutions et pourtant avancer trop lentement si elle ne structure pas son Marketing WEB. Le département doit être capable de transformer son savoir-faire en message simple, crédible et orienté ROI B2B.</p>
          </div>
        </div>
      </div>

      {/* Ce qu'on ne veut pas */}
      <div className="bg-red-500/10 rounded-xl p-6 border border-red-500/30">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <AlertCircle className="w-6 h-6 text-red-400" />
          Ce qu&apos;on ne veut pas
        </h4>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start gap-3">
            <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>Le code pour le code — oublier la raison et l&apos;esprit de l&apos;application qu&apos;on crée.</div>
          </li>
          <li className="flex items-start gap-3">
            <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>Se rendre indispensable.</div>
          </li>
          <li className="flex items-start gap-3">
            <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>Ne pas viser l&apos;excellence.</div>
          </li>
          <li className="flex items-start gap-3">
            <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>Ne pas respecter la gouvernance.</div>
          </li>
          <li className="flex items-start gap-3">
            <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div><strong className="text-white">La complexité inutile.</strong> Ajouter des couches, des outils ou des processus sans valeur réelle nuit à la performance. La simplicité est un choix, pas une faiblesse.</div>
          </li>
        </ul>
      </div>

      {/* Passage Niveau 1 → 2 */}
      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <TrendingUp className="w-6 h-6 text-indigo-400" />
          Vers un département performant et rentable
        </h4>
        <p className="text-gray-300 leading-relaxed mb-4">
          On passe d&apos;un département qui exécute à un département qui structure, optimise et crée de la valeur mesurable.
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          Notre priorité est l&apos;efficacité opérationnelle : mieux travailler, plus vite, avec moins de friction.
        </p>
        <p className="text-gray-300 leading-relaxed">
          Aujourd&apos;hui, l&apos;objectif est clair : bâtir des solutions solides, utiles, et atteindre une rentabilité durable.
        </p>
      </div>

      {/* Objectifs macro */}
      <div className="bg-gradient-to-br from-indigo-500/20 to-purple-500/20 rounded-xl p-6 border-2 border-indigo-500/30">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Calendar className="w-6 h-6 text-indigo-400" />
          Objectifs macro
        </h4>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div><strong className="text-white">150k$/programmeur</strong> en moyenne — rentabilité durable.</div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div><strong className="text-white">Qualité irréprochable</strong> — zéro compromis sur les livrables.</div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div><strong className="text-white">CodeHub</strong> — notre outil pimpé, adoption à 100% de l&apos;équipe.</div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div><strong className="text-white">KPI d&apos;excellence</strong> — mesurer pour améliorer.</div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div><strong className="text-white">IA intégrée</strong> dans tous nos livrables.</div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div><strong className="text-white">Plan Marketing WEB clair</strong> pour Programmer & IA — bénéfices simples, ROI client B2B, traction commerciale plus rapide.</div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div><strong className="text-white">Livraison humaine et professionnelle</strong> — prisme client partout.</div>
          </li>
        </ul>
      </div>

      {/* Phrase clé */}
      <div className="bg-gradient-to-r from-purple-600/20 via-indigo-600/20 to-purple-600/20 rounded-xl p-6 border-2 border-purple-500/30">
        <div className="text-center">
          <p className="text-xl font-semibold text-white leading-relaxed italic">
            On veut créer de la richesse à nos clients.<br />
            Le département qui rend possible pratiquement tout — surtout avec l&apos;IA.<br />
            ROI rapide et efficace.
          </p>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ONGLET 2 — CADRE HOLISTIQUE PRIsME (COACHING)
// ============================================================================
function CadreTab() {
  const [expandedAxe, setExpandedAxe] = useState<number | null>(null);

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-indigo-600/20 rounded-xl p-6 border-2 border-indigo-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-2">
          Cadre holistique – Axes de maturité (0–5)
        </h3>
        <p className="text-gray-300 text-center text-sm">
          Les 8 axes du département. Lecture simple : chaque axe a une intention, un niveau actuel, une cible et des actions immédiates.
        </p>
      </div>

      <div className="grid md:grid-cols-4 gap-3">
        <div className="bg-white/5 rounded-lg p-4 border border-indigo-500/20">
          <div className="text-xs uppercase tracking-wide text-indigo-300 mb-1">1</div>
          <div className="text-sm font-semibold text-white">Lire l&apos;axe</div>
          <div className="text-xs text-gray-400 mt-1">Comprendre son rôle dans le département.</div>
        </div>
        <div className="bg-white/5 rounded-lg p-4 border border-indigo-500/20">
          <div className="text-xs uppercase tracking-wide text-indigo-300 mb-1">2</div>
          <div className="text-sm font-semibold text-white">Voir l&apos;écart</div>
          <div className="text-xs text-gray-400 mt-1">Comparer actuel vs cible.</div>
        </div>
        <div className="bg-white/5 rounded-lg p-4 border border-indigo-500/20">
          <div className="text-xs uppercase tracking-wide text-indigo-300 mb-1">3</div>
          <div className="text-sm font-semibold text-white">Repérer les erreurs</div>
          <div className="text-xs text-gray-400 mt-1">Identifier ce qui freine la maturité.</div>
        </div>
        <div className="bg-white/5 rounded-lg p-4 border border-indigo-500/20">
          <div className="text-xs uppercase tracking-wide text-indigo-300 mb-1">4</div>
          <div className="text-sm font-semibold text-white">Agir maintenant</div>
          <div className="text-xs text-gray-400 mt-1">Exécuter le prochain pas concret.</div>
        </div>
      </div>

      <div className="space-y-4">
        {axesProgIA.map((axe) => {
          const maturite = maturiteData.find(m => m.axe === axe.nom);
          return (
          <div key={axe.id} className="bg-white/5 rounded-xl border border-indigo-500/20 overflow-hidden">
            <button
              onClick={() => setExpandedAxe(expandedAxe === axe.id ? null : axe.id)}
              className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white font-bold text-sm">
                  {axe.id}
                </div>
                <div>
                  <h4 className="text-lg font-bold text-white">{axe.nom}</h4>
                  {maturite && (
                    <p className="text-xs text-gray-400 mt-1">
                      <span className="text-amber-400">Actuel: {maturite.actuel}</span>
                      <span className="mx-1">→</span>
                      <span className="text-emerald-400">Cible: {maturite.cible}</span>
                    </p>
                  )}
                  {axe.avatars && axe.avatars.length > 0 && (
                    <div className="flex items-center gap-1.5 mt-1">
                      {axe.avatars.map((avatarName, idx) => {
                        const avatarSrc = avatarMap[avatarName];
                        return avatarSrc ? (
                          avatarSrc === '$' ? (
                            <div 
                              key={idx}
                              className="w-5 h-5 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 border border-indigo-400 flex items-center justify-center text-white font-bold text-[10px] shadow-sm"
                              title={avatarName}
                            >
                              $
                            </div>
                          ) : avatarSrc === '👤' ? (
                            <div 
                              key={idx}
                              className="w-5 h-5 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 border border-indigo-400 flex items-center justify-center text-white text-xs shadow-sm"
                              title={avatarName}
                            >
                              👤
                            </div>
                          ) : (
                            <img 
                              key={idx}
                              src={avatarSrc} 
                              alt={avatarName}
                              className="w-5 h-5 rounded-full border border-indigo-400 object-contain bg-white/10"
                              title={avatarName}
                            />
                          )
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
              </div>
              <div className={`transform transition-transform ${expandedAxe === axe.id ? 'rotate-180' : ''}`}>
                <X className="w-5 h-5 text-gray-400" />
              </div>
            </button>

            {expandedAxe === axe.id && (
              <div className="p-6 border-t border-indigo-500/20 space-y-4">
                {/* Sous-piliers */}
                {axe.sousPiliers && axe.sousPiliers.length > 0 && (
                  <div>
                    <h5 className="text-sm font-bold text-cyan-400 mb-2 flex items-center gap-2">
                      <Shield className="w-4 h-4" />
                      Sous-piliers
                    </h5>
                    <div className="flex flex-wrap gap-2">
                      {axe.sousPiliers.map((piller, idx) => (
                        <span key={idx} className="px-2 py-1 bg-cyan-500/20 text-cyan-300 text-xs rounded border border-cyan-500/30">
                          {piller}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Intention */}
                <div>
                  <h5 className="text-sm font-bold text-indigo-400 mb-2 flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    Intention
                  </h5>
                  <p className="text-gray-300 text-sm leading-relaxed">{axe.intention}</p>
                </div>

                {/* Pourquoi critique */}
                <div>
                  <h5 className="text-sm font-bold text-yellow-400 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Pourquoi c'est critique
                  </h5>
                  <p className="text-gray-300 text-sm leading-relaxed">{axe.pourquoiCritique}</p>
                </div>

                {/* Concrètement BZ */}
                <div>
                  <h5 className="text-sm font-bold text-emerald-400 mb-2 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Concrètement chez BZ
                  </h5>
                  <p className="text-gray-300 text-sm leading-relaxed">{axe.concretementBZ}</p>
                </div>

                {/* Erreurs fréquentes */}
                <div>
                  <h5 className="text-sm font-bold text-red-400 mb-2 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Erreurs fréquentes
                  </h5>
                  <ul className="space-y-2">
                    {axe.erreursFrequentes.map((erreur, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-gray-300 text-sm">
                        <X className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                        <span>{erreur}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Niveaux de maturité */}
                <div>
                  <h5 className="text-sm font-bold text-purple-400 mb-3 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4" />
                    Niveaux 0 → 5
                  </h5>
                  <div className="space-y-2">
                    {axe.niveaux.map((n) => (
                      <div key={n.niveau} className="bg-slate-800/50 rounded-lg p-3 border border-indigo-500/20">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-bold text-indigo-400">{n.niveau}</span>
                          <span className="text-xs text-gray-500">•</span>
                          <span className="text-xs font-semibold text-purple-300">{n.label}</span>
                        </div>
                        <p className="text-gray-300 text-xs leading-relaxed">{n.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* À faire maintenant */}
                {axe.aFaireMaintenant && axe.aFaireMaintenant.length > 0 && (
                  <div>
                    <h5 className="text-sm font-bold text-blue-400 mb-2 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      À faire maintenant (N+1)
                    </h5>
                    <ul className="space-y-2">
                      {axe.aFaireMaintenant.map((action, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-gray-300 text-sm">
                          <CheckCircle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                          <span>{action}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Preuves visibles */}
                {axe.preuvesVisibles && axe.preuvesVisibles.length > 0 && (
                  <div>
                    <h5 className="text-sm font-bold text-emerald-400 mb-2 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      Preuves visibles
                    </h5>
                    <div className="flex flex-wrap gap-2">
                      {axe.preuvesVisibles.map((preuve, idx) => (
                        <span key={idx} className="px-2 py-1 bg-emerald-500/20 text-emerald-300 text-xs rounded border border-emerald-500/30">
                          {preuve}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
        })}
      </div>

      {/* Phrase de clôture */}
      <div className="bg-gradient-to-r from-purple-600/20 via-indigo-600/20 to-purple-600/20 rounded-xl p-6 border-2 border-purple-500/30">
        <div className="text-center">
          <p className="text-lg font-semibold text-white leading-relaxed italic">
            Chaque axe PRIsME est une responsabilité collective,<br />
            mais une redevabilité claire.<br />
            La maturité ne s'improvise pas, elle se gouverne.
          </p>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ONGLET 3 — VUE MATURITÉ
// ============================================================================
function MaturiteTab() {
  // Convertir pour le graphique radar (échelle 0-5 vers 0-100)
  const chartData = maturiteData.map(item => ({
    category: item.axe.length > 15 ? item.axe.substring(0, 15) + '...' : item.axe,
    Actuel: (item.actuel / 5) * 100,
    Cible: (item.cible / 5) * 100,
  }));

  const actuelAverage = (maturiteData.reduce((sum, item) => sum + item.actuel, 0) / maturiteData.length).toFixed(1);
  const cibleAverage = (maturiteData.reduce((sum, item) => sum + item.cible, 0) / maturiteData.length).toFixed(1);
  const biggestGaps = [...maturiteData]
    .map(item => ({ ...item, gap: item.cible - item.actuel }))
    .sort((a, b) => b.gap - a.gap)
    .slice(0, 3);

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-indigo-600/20 rounded-xl p-6 border-2 border-indigo-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-2">
          Maturité (Spider + Bar)
        </h3>
        <p className="text-gray-300 text-center text-sm">
          Où on est, où on veut aller et quels axes doivent progresser pour rendre le département plus fort d&apos;ici fin 2026.
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-white/5 rounded-xl p-4 border border-indigo-500/20">
          <div className="text-sm font-semibold text-white">Lecture 1 — AS-IS</div>
          <p className="text-xs text-gray-400 mt-1">Le score actuel montre le niveau réel de structuration du département aujourd&apos;hui.</p>
        </div>
        <div className="bg-white/5 rounded-xl p-4 border border-indigo-500/20">
          <div className="text-sm font-semibold text-white">Lecture 2 — TARGET</div>
          <p className="text-xs text-gray-400 mt-1">La cible indique le niveau de maîtrise attendu pour soutenir l&apos;excellence et la rentabilité.</p>
        </div>
        <div className="bg-white/5 rounded-xl p-4 border border-emerald-500/20">
          <div className="text-sm font-semibold text-white">Lecture 3 — Priorités</div>
          <p className="text-xs text-gray-400 mt-1">Plus l&apos;écart est grand, plus l&apos;axe demande de focus, de gouvernance et de discipline.</p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        {biggestGaps.map((item) => (
          <div key={item.axe} className="bg-white/5 rounded-xl p-4 border border-indigo-500/20">
            <div className="text-xs uppercase tracking-wide text-indigo-300 mb-1">Priorité</div>
            <div className="text-sm font-semibold text-white">{item.axe}</div>
            <div className="text-xs text-gray-400 mt-2">Actuel {item.actuel}/5 → Cible {item.cible}/5</div>
            <div className="text-xs text-emerald-300 mt-1">Écart : +{item.gap}</div>
          </div>
        ))}
      </div>

      {/* Scores moyens */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-4">
          <div className="text-sm text-yellow-300 font-medium">Score Actuel (AS-IS)</div>
          <div className="text-2xl font-bold text-yellow-200">{actuelAverage}/5</div>
          <div className="text-xs text-yellow-400">État actuel</div>
        </div>
        <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-lg p-4">
          <div className="text-sm text-emerald-300 font-medium">Score Cible (TARGET)</div>
          <div className="text-2xl font-bold text-emerald-200">{cibleAverage}/5</div>
          <div className="text-xs text-emerald-400">Objectif fin 2026</div>
        </div>
      </div>

      {/* Graphique Spider */}
      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4">Graphique Radar - Maturité Programmation & IA</h4>
        <div className="h-96 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart data={chartData} margin={{ top: 20, right: 80, bottom: 20, left: 80 }}>
              <PolarGrid stroke="#4b5563" />
              <PolarAngleAxis 
                dataKey="category" 
                tick={{ fontSize: 11, fill: '#d1d5db' }}
              />
              <PolarRadiusAxis 
                angle={90} 
                domain={[0, 100]} 
                tick={{ fontSize: 10, fill: '#9ca3af' }}
                tickCount={6}
              />
              
              <Radar
                name="Actuel (AS-IS)"
                dataKey="Actuel"
                stroke="#fbbf24"
                fill="#fbbf24"
                fillOpacity={0.2}
                strokeWidth={3}
                dot={{ fill: '#fbbf24', strokeWidth: 2, r: 4 }}
              />
              
              <Radar
                name="Cible (TARGET)"
                dataKey="Cible"
                stroke="#10b981"
                fill="#10b981"
                fillOpacity={0.1}
                strokeWidth={2}
                strokeDasharray="5 5"
                dot={{ fill: '#10b981', strokeWidth: 2, r: 3 }}
              />
              
              <Legend 
                wrapperStyle={{ paddingTop: '20px', color: '#d1d5db' }}
                iconType="circle"
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Bar Chart */}
      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4">Bar Chart - Détail par axe</h4>
        <div className="h-96 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={maturiteData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#4b5563" />
              <XAxis 
                dataKey="axe" 
                angle={-45} 
                textAnchor="end" 
                height={120}
                tick={{ fontSize: 10, fill: '#d1d5db' }}
              />
              <YAxis 
                domain={[0, 5]}
                tick={{ fontSize: 10, fill: '#d1d5db' }}
              />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #4b5563', borderRadius: '8px' }}
                labelStyle={{ color: '#d1d5db' }}
              />
              <Legend />
              <Bar dataKey="actuel" fill="#fbbf24" name="Actuel (AS-IS)" />
              <Bar dataKey="cible" fill="#10b981" name="Cible (TARGET)" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Échelle */}
      <div className="bg-white/5 rounded-xl p-4 border border-indigo-500/20">
        <h4 className="text-lg font-bold text-white mb-3">Échelle de maturité</h4>
        <ul className="space-y-2 text-gray-300 text-sm">
          <li className="flex items-center gap-2">
            <span className="font-semibold text-indigo-400">0 =</span>
            <span>Inexistant</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="font-semibold text-indigo-400">1 =</span>
            <span>POC / Expérimentation</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="font-semibold text-indigo-400">2 =</span>
            <span>Adoption structurée</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="font-semibold text-indigo-400">3 =</span>
            <span>Maturité opérationnelle</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="font-semibold text-indigo-400">4 =</span>
            <span>Excellence</span>
          </li>
          <li className="flex items-center gap-2">
            <span className="font-semibold text-indigo-400">5 =</span>
            <span>Référence marché</span>
          </li>
        </ul>
      </div>
    </div>
  );
}

// ============================================================================
// ONGLET 4 — RÔLES & RESPONSABILITÉS (RACI)
// ============================================================================
function RACITab() {
  const [guidanceModal, setGuidanceModal] = useState<{
    activite: string;
    roleLetter: 'A' | 'R' | 'C' | 'I';
    personNames: string[];
  } | null>(null);
  const [activiteDetailModal, setActiviteDetailModal] = useState<{ activite: string; pillier: string; detail: string } | null>(null);

  const raciColors = {
    R: 'bg-blue-500/20 border-blue-500/50 text-blue-300',
    A: 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300',
    C: 'bg-yellow-500/20 border-yellow-500/50 text-yellow-300',
    I: 'bg-purple-500/20 border-purple-500/50 text-purple-300'
  };

  const raciLabels = {
    R: 'Responsable (fait le travail)',
    A: 'Accountable (décide et répond)',
    C: 'Consulté (donne son avis)',
    I: 'Informé (est tenu au courant)'
  };

  // Extraire tous les rôles uniques et les trier (personnes avant groupes)
  const allRoles = new Set<string>();
  raciData.forEach(activity => {
    Object.keys(activity.roles).forEach(role => allRoles.add(role));
  });
  
  const groupes = ['Comité PRIsME', 'Directeurs de compte', 'Gestionnaire', 'Primes'];
  const personnes = Array.from(allRoles).filter(role => !groupes.includes(role));
  const groupesPresents = Array.from(allRoles).filter(role => groupes.includes(role));
  
  // Trier : Dany, Eric, Guillaume, Marc-Antoine, Programmeur, autres
  const ordreSouhaite = ['Dany Bourassa', 'Eric Durand', 'Guillaume Cloutier', 'Marc-Antoine Caron', 'Programmeur'];
  const personnesTriees = Array.from(personnes).sort();
  const personnesOrdonnees: string[] = [];
  ordreSouhaite.forEach(p => { if (personnesTriees.includes(p)) personnesOrdonnees.push(p); });
  personnesTriees.filter(p => !ordreSouhaite.includes(p)).sort().forEach(p => personnesOrdonnees.push(p));
  
  // Ajouter les groupes (triés)
  const uniqueRoles = [...personnesOrdonnees, ...groupesPresents.sort()];

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-indigo-600/20 rounded-xl p-6 border-2 border-indigo-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-2">
          Rôles & responsabilités (RACI)
        </h3>
        <p className="text-gray-300 text-center text-sm">
          Lecture simple : qui décide, qui exécute, qui conseille et qui doit seulement être informé.
        </p>
      </div>

      <div className="grid md:grid-cols-4 gap-4">
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4">
          <div className="text-xs uppercase tracking-wide text-emerald-300 mb-1">A — Accountable</div>
          <div className="text-sm font-semibold text-white">Eric Durand</div>
          <p className="text-xs text-gray-400 mt-1">Répond du résultat global, tranche et arbitre.</p>
        </div>
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
          <div className="text-xs uppercase tracking-wide text-blue-300 mb-1">R — Responsables</div>
          <div className="text-sm font-semibold text-white">Guillaume / Marco / Programmeurs</div>
          <p className="text-xs text-gray-400 mt-1">Exécutent, livrent et font avancer le terrain.</p>
        </div>
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
          <div className="text-xs uppercase tracking-wide text-amber-300 mb-1">C — Consulté</div>
          <div className="text-sm font-semibold text-white">Dany + comité</div>
          <p className="text-xs text-gray-400 mt-1">Challenge la direction et contribue aux décisions majeures.</p>
        </div>
        <div className="bg-purple-500/10 border border-purple-500/30 rounded-xl p-4">
          <div className="text-xs uppercase tracking-wide text-purple-300 mb-1">I — Informé</div>
          <div className="text-sm font-semibold text-white">Parties prenantes</div>
          <p className="text-xs text-gray-400 mt-1">Reçoit l’information, suit les impacts et les arbitrages.</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-4 gap-4">
        <div className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-500/30">
          <div className="text-xs uppercase tracking-wide text-emerald-300 mb-1">Décideur</div>
          <div className="text-lg font-bold text-white">Eric Durand</div>
          <p className="text-xs text-gray-400 mt-2">Owner du département. Il tranche, priorise et répond du résultat final.</p>
        </div>
        <div className="bg-blue-500/10 rounded-xl p-4 border border-blue-500/30">
          <div className="text-xs uppercase tracking-wide text-blue-300 mb-1">Exécution</div>
          <div className="text-lg font-bold text-white">Guillaume & Marco</div>
          <p className="text-xs text-gray-400 mt-2">Ils portent l’exécution terrain, la qualité, la livraison et la traction quotidienne.</p>
        </div>
        <div className="bg-amber-500/10 rounded-xl p-4 border border-amber-500/30">
          <div className="text-xs uppercase tracking-wide text-amber-300 mb-1">Arbitrage stratégique</div>
          <div className="text-lg font-bold text-white">Dany Bourassa</div>
          <p className="text-xs text-gray-400 mt-2">Il fixe le cap et arbitre les décisions majeures, sans gérer l’opérationnel quotidien.</p>
        </div>
        <div className="bg-indigo-500/10 rounded-xl p-4 border border-indigo-500/30">
          <div className="text-xs uppercase tracking-wide text-indigo-300 mb-1">Livraison terrain</div>
          <div className="text-lg font-bold text-white">Programmeurs</div>
          <p className="text-xs text-gray-400 mt-2">Ils livrent, appliquent les standards et remontent rapidement les blocages.</p>
        </div>
      </div>

      <details className="bg-white/5 rounded-xl border border-indigo-500/20 overflow-hidden">
        <summary className="cursor-pointer list-none p-4 flex items-center justify-between hover:bg-white/5 transition-colors">
          <div>
            <div className="text-white font-semibold">Afficher la matrice détaillée RACI</div>
            <div className="text-xs text-gray-400 mt-1">Ouvre le détail activité par activité, avec guide R / A / C / I.</div>
          </div>
          <span className="text-xs px-3 py-1 rounded-full bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">Détail</span>
        </summary>

        <div className="p-4 pt-0 space-y-4">
          {/* Légende RACI */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Object.entries(raciLabels).map(([key, label]) => (
              <div key={key} className={`${raciColors[key as keyof typeof raciColors]} rounded-lg p-3 border-2 text-center`}>
                <div className="font-bold text-lg mb-1">{key}</div>
                <div className="text-xs">{label}</div>
              </div>
            ))}
          </div>

          {/* Tableau RACI */}
          <div className="bg-white/5 rounded-xl border border-indigo-500/20 overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-indigo-500/30">
                  <th className="text-left p-4 text-white font-bold">Activité</th>
                  <th className="text-left p-4 text-white font-bold min-w-[180px]">Pilier associé</th>
                  {uniqueRoles.map(role => (
                    <th key={role} className="text-center p-4 text-white font-bold min-w-[120px]">
                      <div className="flex flex-col items-center gap-2">
                        {avatarMap[role] ? (
                          avatarMap[role] === '$' ? (
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 border-2 border-indigo-400 flex items-center justify-center text-white font-bold text-sm shadow-lg">
                              $
                            </div>
                          ) : !avatarMap[role].startsWith('/') ? (
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 border-2 border-indigo-400 flex items-center justify-center text-white text-lg shadow-lg">
                              {avatarMap[role]}
                            </div>
                          ) : (
                            <img 
                              src={avatarMap[role]} 
                              alt={role}
                              className="w-8 h-8 rounded-full border-2 border-indigo-400 object-contain bg-white/10"
                            />
                          )
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-indigo-500/20 border-2 border-indigo-400 flex items-center justify-center text-xs text-white">
                            {role.split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </div>
                        )}
                        <span className="text-xs">{role}</span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {raciData.map((activity, idx) => (
                  <tr key={idx} className="border-b border-indigo-500/20 hover:bg-white/5">
                    <td className="p-4">
                      <button
                        type="button"
                        onClick={() => setActiviteDetailModal({
                          activite: activity.activite,
                          pillier: (activity as { pillier?: string }).pillier || '',
                          detail: (activity as { detail?: string }).detail || ''
                        })}
                        className="text-left text-gray-300 font-medium hover:text-indigo-300 flex items-center gap-2 w-full"
                      >
                        <span className="flex-1">{activity.activite}</span>
                        <span className="text-xs px-2 py-1 rounded bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 shrink-0">
                          Détail
                        </span>
                      </button>
                    </td>
                    <td className="p-4">
                      <span className="text-xs px-2 py-1 rounded bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                        {(activity as { pillier?: string }).pillier || '—'}
                      </span>
                    </td>
                    {uniqueRoles.map(role => {
                      const raciValue = (activity.roles as unknown as Record<string, string>)[role] || '';
                      const letter = raciValue as 'A' | 'R' | 'C' | 'I' | '';
                      const isClickable = letter && ['A', 'R', 'C', 'I'].includes(letter);
                      return (
                        <td
                          key={role}
                          className={`text-center p-4 ${isClickable ? 'cursor-pointer hover:bg-indigo-500/10' : ''}`}
                          onClick={() => isClickable && setGuidanceModal({
                            activite: activity.activite,
                            roleLetter: letter,
                            personNames: uniqueRoles.filter(r => (activity.roles as unknown as Record<string, string>)[r] === letter)
                          })}
                        >
                          {raciValue && (
                            <span className={`inline-block px-3 py-1 rounded-full font-bold text-sm border-2 ${raciColors[raciValue as keyof typeof raciColors]}`} title={isClickable ? 'Cliquer pour voir le guide R/A/C' : ''}>
                              {raciValue}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-gray-400 text-center">
            💡 <strong>Activité</strong> : clique « Détail » pour voir ce qu&apos;elle implique. <strong>Lettre R/A/C</strong> : clique pour le guide personnalisé.
          </p>
        </div>
      </details>

      {/* Modal Détail Activité */}
      {activiteDetailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setActiviteDetailModal(null)}>
          <div className="bg-slate-900 rounded-xl border-2 border-indigo-500/50 max-w-xl w-full shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h4 className="text-xl font-bold text-white">{activiteDetailModal.activite}</h4>
                  <span className="inline-block mt-2 text-xs px-2 py-1 rounded bg-indigo-500/20 text-indigo-300 border border-indigo-500/30">
                    {activiteDetailModal.pillier}
                  </span>
                </div>
                <button onClick={() => setActiviteDetailModal(null)} className="text-gray-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="mt-4">
                <h5 className="text-sm font-bold text-indigo-400 mb-2">Ce que cette activité implique</h5>
                <p className="text-gray-300 text-sm leading-relaxed">{activiteDetailModal.detail}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Guidance RACI */}
      {guidanceModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setGuidanceModal(null)}>
          <div className="bg-slate-900 rounded-xl border-2 border-indigo-500/50 max-w-lg w-full max-h-[85vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h4 className="text-lg font-bold text-white">{guidanceModal.activite}</h4>
                  <div className="flex items-center gap-2 mt-2">
                    <span className={`px-3 py-1 rounded-full font-bold text-sm border-2 ${raciColors[guidanceModal.roleLetter]}`}>
                      {guidanceModal.roleLetter} — {raciLabels[guidanceModal.roleLetter]}
                    </span>
                    <span className="text-xs text-gray-400">
                      {guidanceModal.personNames.join(', ')}
                    </span>
                  </div>
                </div>
                <button onClick={() => setGuidanceModal(null)} className="text-gray-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>
              {(() => {
                const g = raciGuidance[guidanceModal.activite]?.[guidanceModal.roleLetter] ?? raciGuidanceGeneric[guidanceModal.roleLetter];
                return (
                  <div className="space-y-4">
                    <div>
                      <h5 className="text-sm font-bold text-emerald-400 mb-2">✅ À faire</h5>
                      <ul className="space-y-1">
                        {g.doit.map((item, i) => (
                          <li key={i} className="text-gray-300 text-sm flex gap-2">
                            <CheckCircle className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h5 className="text-sm font-bold text-red-400 mb-2">❌ Ne pas faire</h5>
                      <ul className="space-y-1">
                        {g.nePasFaire.map((item, i) => (
                          <li key={i} className="text-gray-300 text-sm flex gap-2">
                            <X className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h5 className="text-sm font-bold text-amber-400 mb-2">⚠️ Pièges à éviter</h5>
                      <ul className="space-y-1">
                        {g.pieges.map((item, i) => (
                          <li key={i} className="text-gray-300 text-sm flex gap-2">
                            <AlertCircle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Rôles macro — Département Programmation & IA */}
      <div className="rounded-xl p-4 border-2 border-indigo-500/30 bg-indigo-500/5">
        <h4 className="text-lg font-bold text-white mb-3">Vue exécutive — Qui fait quoi</h4>
        <p className="text-gray-400 text-sm mb-4">Commence ici pour comprendre les responsabilités macro. Utilise ensuite la matrice RACI si tu veux le détail activité par activité.</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-white/5 rounded-xl p-4 border border-emerald-500/30">
          <div className="flex items-center gap-2 mb-3">
            <img src="/ED.png" alt="Eric Durand" className="w-10 h-10 rounded-full border-2 border-emerald-500/50 object-contain bg-white/10" />
            <div>
              <h4 className="text-lg font-bold text-white">Eric Durand</h4>
              <span className="text-xs text-emerald-400 font-medium">Dirigeant département — Accountable</span>
            </div>
          </div>
          <p className="text-gray-300 text-sm font-medium mb-2">Owner du département. Il décide, arbitre et répond du résultat global.</p>
          <ul className="text-gray-400 text-xs space-y-1">
            <li>• Possède la vision, la stratégie et la priorisation</li>
            <li>• Possède la rentabilité, les arbitrages RH et la trajectoire du département</li>
            <li>• Valide la gouvernance, les escalades et les grandes décisions</li>
            <li>• Ne porte pas le quotidien opérationnel à la place de l’équipe</li>
          </ul>
        </div>
        <div className="bg-white/5 rounded-xl p-4 border border-blue-500/30">
          <div className="flex items-center gap-2 mb-3">
            <img src="/GC.png" alt="Guillaume Cloutier" className="w-10 h-10 rounded-full border-2 border-blue-500/50 object-contain bg-white/10" />
            <div>
              <h4 className="text-lg font-bold text-white">Guillaume Cloutier</h4>
              <span className="text-xs text-blue-400 font-medium">Responsable exécution — Charge de projet & Qualité</span>
            </div>
          </div>
          <p className="text-gray-300 text-sm font-medium mb-2">Leader d’exécution. Il pilote les projets, la qualité et donne le rythme terrain. <span className="text-indigo-300 italic">Il démontre par l’exemple.</span></p>
          <ul className="text-gray-400 text-xs space-y-1">
            <li>• Porte l’exécution, le suivi et la charge projet</li>
            <li>• Garantit la qualité des livrables et la discipline terrain</li>
            <li>• Soutient l’onboarding, les 1:1 et la progression de l’équipe</li>
            <li>• Ne décide pas seul de la vision globale du département</li>
          </ul>
        </div>
        <div className="bg-white/5 rounded-xl p-4 border border-blue-500/30">
          <div className="flex items-center gap-2 mb-3">
            <img src="/MAC.png" alt="Marc-Antoine Caron" className="w-10 h-10 rounded-full border-2 border-blue-500/50 object-contain bg-white/10" />
            <div>
              <h4 className="text-lg font-bold text-white">Marc-Antoine Caron</h4>
              <span className="text-xs text-blue-400 font-medium">Responsable exécution — Technique & Client</span>
            </div>
          </div>
          <p className="text-gray-300 text-sm font-medium mb-2">Leader d’exécution avec dominante client. Il porte la technique, la relation terrain et la livraison. <span className="text-indigo-300 italic">Il démontre par l’exemple.</span></p>
          <ul className="text-gray-400 text-xs space-y-1">
            <li>• Porte la technique, l’infra et la cohérence de livraison</li>
            <li>• Porte la cohérence technique et soutient la livraison côté architecture / IA</li>
            <li>• Contribue fortement à l’innovation IA et à l’architecture</li>
            <li>• Ne remplace pas le rôle de direction ni l’arbitrage final</li>
          </ul>
        </div>
        <div className="bg-white/5 rounded-xl p-4 border border-amber-500/30">
          <div className="flex items-center gap-2 mb-3">
            <img src="/DB.png" alt="Dany Bourassa" className="w-10 h-10 rounded-full border-2 border-amber-500/50 object-contain bg-white/10" />
            <div>
              <h4 className="text-lg font-bold text-white">Dany Bourassa</h4>
              <span className="text-xs text-amber-400 font-medium">Vision stratégique — Arbitrage</span>
            </div>
          </div>
          <p className="text-gray-300 text-sm font-medium mb-2">Fixe le cap et arbitre les décisions majeures. Il reste au niveau stratégique, pas dans l’opérationnel quotidien.</p>
          <ul className="text-gray-400 text-xs space-y-1">
            <li>• Porte la vision et l’alignement global avec BZ</li>
            <li>• Arbitre les conflits, priorités et escalades importantes</li>
            <li>• Intervient sur les décisions majeures, pas sur le micro-management</li>
            <li>• Ne remplace pas Eric dans la direction quotidienne du département</li>
          </ul>
        </div>
        <div className="bg-white/5 rounded-xl p-4 border border-indigo-500/30 md:col-span-2">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-10 h-10 rounded-full border-2 border-indigo-500/50 bg-indigo-500/20 flex items-center justify-center text-xl">👤</div>
            <div>
              <h4 className="text-lg font-bold text-white">Programmeur</h4>
              <span className="text-xs text-indigo-400 font-medium">Exécution terrain — Livraison</span>
            </div>
          </div>
          <p className="text-gray-300 text-sm font-medium mb-2">Livre avec qualité dans un cadre clair. Il exécute, contribue et remonte rapidement les blocages.</p>
          <ul className="text-gray-400 text-xs space-y-1">
            <li>• Respecte les standards, la DoD, la QA et les outils communs</li>
            <li>• Livre à temps et rend son avancement visible</li>
            <li>• Contribue activement aux estimations, démos et améliorations</li>
            <li>• N’a pas à porter seul les arbitrages de direction</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ONGLET 5 — ROADMAP ANNUELLE 2026
// ============================================================================
function RoadmapTab() {
  const [roadmapMode, setRoadmapMode] = useState<'execution' | 'strategie'>('execution');
  const [isEditingPMO, setIsEditingPMO] = useState(false);
  const [pmoProgress, setPmoProgress] = useState<Record<string, number>>({});
  const [pmoQuarterOverrides, setPmoQuarterOverrides] = useState<
    Record<string, { theme?: string; totalHeures?: number; synthese?: string[] }>
  >({});
  const [pmoTaskOverrides, setPmoTaskOverrides] = useState<
    Record<string, Partial<RoadmapOperationnelTask>>
  >({});
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [pmoHistory, setPmoHistory] = useState<
    Array<{
      ts: string;
      user?: string;
      kind: 'task_progress' | 'task_meta' | 'quarter_meta';
      quarter: string;
      axeId?: string;
      taskIdx?: number;
      label: string;
      from: string;
      to: string;
    }>
  >([]);

  const lastSavedRef = useRef<{
    progress: Record<string, number>;
    quarters: Record<string, { theme?: string; totalHeures?: number; synthese?: string[] }>;
    tasks: Record<string, Partial<RoadmapOperationnelTask>>;
  }>({ progress: {}, quarters: {}, tasks: {} });

  const getTaskKey = (quarterTrimestre: string, axeId: string, idx: number) => `${quarterTrimestre}::${axeId}::${idx}`;

  // Charger l'état PMO depuis la DB (partagé)
  useEffect(() => {
    (async () => {
      try {
        const state = await getRoadmapPmoState();
        const progress = state.progress || {};
        const taskOverrides = state.tasks || {};
        
        // Initialiser TACTICE intégré à 100% si pas déjà défini
        const tacticeKey = 'Q1 2026::CONTENU_METHODE::0';
        if (!(tacticeKey in progress)) {
          progress[tacticeKey] = 100;
        }
        // Initialiser le critère de validation pour TACTICE intégré
        if (!(tacticeKey in taskOverrides) || !taskOverrides[tacticeKey].critere) {
          taskOverrides[tacticeKey] = {
            ...taskOverrides[tacticeKey],
            critere: 'Onglet Projet créé et 90% fonctionnel'
          };
        }
        
        // Initialiser PRISME Employé V1 (60 %) à 50% si pas déjà défini
        const prismeEmployeKey = 'Q1 2026::CONTENU_METHODE::2';
        if (!(prismeEmployeKey in progress)) {
          progress[prismeEmployeKey] = 50;
        }
        
        // Initialiser PRISME Département V1 à 50% si pas déjà défini
        const prismeDepartementKey = 'Q1 2026::CONTENU_METHODE::3';
        if (!(prismeDepartementKey in progress)) {
          progress[prismeDepartementKey] = 50;
        }
        
        // Initialiser Gestion droits & accès à 100% si pas déjà défini
        const gestionDroitsKey = 'Q2 2026::PRISME_EMPLOYE_METHODE::2';
        if (!(gestionDroitsKey in progress)) {
          progress[gestionDroitsKey] = 100;
        }
        
        // Initialiser BOT PRISME V1 à 50% si pas déjà défini
        const botPrismeKey = 'Q2 2026::IA_BOT::0';
        if (!(botPrismeKey in progress)) {
          progress[botPrismeKey] = 50;
        }
        
        // Initialiser Suivi projet IA à 75% si pas déjà défini
        const suiviProjetIAKey = 'Q2 2026::IA_BOT::1';
        if (!(suiviProjetIAKey in progress)) {
          progress[suiviProjetIAKey] = 75;
        }
        
        setPmoProgress(progress);
        setPmoQuarterOverrides(state.quarters || {});
        setPmoTaskOverrides(taskOverrides);
        setPmoHistory(state.history || []);
        lastSavedRef.current = {
          progress: progress,
          quarters: state.quarters || {},
          tasks: taskOverrides,
        };
      } catch (e) {
        console.error('Erreur chargement roadmap PMO (DB):', e);
      }
    })();
  }, []);

  const savePMOState = async () => {
    try {
      const state = await saveRoadmapPmoState({
        progress: pmoProgress,
        quarters: pmoQuarterOverrides,
        tasks: pmoTaskOverrides,
      });
      setPmoProgress(state.progress || {});
      setPmoQuarterOverrides(state.quarters || {});
      setPmoTaskOverrides(state.tasks || {});
      setPmoHistory(state.history || []);
      lastSavedRef.current = {
        progress: state.progress || {},
        quarters: state.quarters || {},
        tasks: state.tasks || {},
      };
    } catch (e) {
      console.error('Erreur sauvegarde roadmap PMO (DB):', e);
    }
  };

  const clampPct = (v: unknown) => {
    const n = typeof v === 'number' ? v : Number(v || 0);
    return Math.max(0, Math.min(100, Number.isFinite(n) ? n : 0));
  };

  const getTaskPct = (quarterTrimestre: string, axeId: string, idx: number) =>
    clampPct(pmoProgress[getTaskKey(quarterTrimestre, axeId, idx)] ?? 0);

  const getQuarterWeight = (quarter: RoadmapOperationnelQuarter) => {
    // Pondération principale = total heures du trimestre (éditable). Sinon fallback = somme des heures des tâches.
    const overrideHours = pmoQuarterOverrides[quarter.trimestre]?.totalHeures;
    const base = typeof overrideHours === 'number' && Number.isFinite(overrideHours)
      ? overrideHours
      : typeof quarter.totalHeures === 'number' && Number.isFinite(quarter.totalHeures)
        ? quarter.totalHeures
        : undefined;
    if (typeof base === 'number') return Math.max(0, base);

    let sum = 0;
    quarter.axes.forEach((axe) => {
      axe.tasks.forEach((t) => {
        const h = typeof t.heures === 'number' && Number.isFinite(t.heures) ? t.heures : 0;
        sum += h > 0 ? h : 1;
      });
    });
    return sum;
  };

  const quarterStats = useMemo(() => {
    const stats: Record<string, { pct: number; weight: number }> = {};

    roadmapOperationnel2026.forEach((quarter) => {
      let total = 0;
      let weighted = 0;

      quarter.axes.forEach((axe) => {
        axe.tasks.forEach((t, idx) => {
          const h = typeof t.heures === 'number' && Number.isFinite(t.heures) ? t.heures : 0;
          const w = h > 0 ? h : 1;
          const pct = getTaskPct(quarter.trimestre, axe.id, idx);
          total += w;
          weighted += w * pct;
        });
      });

      const pct = total > 0 ? Math.round(weighted / total) : 0;
      const weight = getQuarterWeight(quarter);
      stats[quarter.trimestre] = { pct, weight };
    });

    return stats;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pmoProgress, pmoQuarterOverrides]);

  const globalPct = useMemo(() => {
    // Global = moyenne pondérée des % de chaque trimestre (Q1-Q4)
    let total = 0;
    let weighted = 0;
    roadmapOperationnel2026.forEach((quarter) => {
      const s = quarterStats[quarter.trimestre] || { pct: 0, weight: 0 };
      const w = typeof s.weight === 'number' && Number.isFinite(s.weight) ? s.weight : 0;
      total += w;
      weighted += w * s.pct;
    });
    if (total <= 0) return 0;
    return Math.round(weighted / total);
  }, [quarterStats]);

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-indigo-600/20 rounded-xl p-6 border-2 border-indigo-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-2">
          Roadmap 2026
        </h3>
        <p className="text-gray-300 text-center text-sm">
          Deux vues complémentaires : exécution (PMO) et stratégie (capabilités)
        </p>
      </div>

      {/* Sélecteur de vue */}
      <div className="flex items-center justify-center">
        <div className="inline-flex rounded-xl border border-indigo-500/30 bg-slate-900/40 p-1">
          <button
            type="button"
            onClick={() => setRoadmapMode('execution')}
            className={`px-4 py-2 text-sm font-bold rounded-lg transition ${
              roadmapMode === 'execution'
                ? 'bg-indigo-500/30 text-white border border-indigo-500/50'
                : 'text-indigo-200 hover:text-white'
            }`}
          >
            Exécution (PMO)
          </button>
          <button
            type="button"
            onClick={() => setRoadmapMode('strategie')}
            className={`px-4 py-2 text-sm font-bold rounded-lg transition ${
              roadmapMode === 'strategie'
                ? 'bg-indigo-500/30 text-white border border-indigo-500/50'
                : 'text-indigo-200 hover:text-white'
            }`}
          >
            Stratégie (boussole)
          </button>
        </div>
      </div>

      {roadmapMode === 'execution' && (
        <div className="space-y-6">
          <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <h4 className="text-xl font-bold text-white mb-2">Roadmap opérationnel 2026 (équipe)</h4>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Document de pilotage opérationnel (PMO) : <strong className="text-white">qui fait quoi</strong>, <strong className="text-white">quand</strong>,
                  et <strong className="text-white">comment on valide</strong>. Conçu pour avancer vite et mesurer l’avancement par jalons.
                </p>
                <div className="mt-4">
                  <div className="flex items-center justify-between text-xs text-gray-300">
                    <span className="font-semibold">Avancement global</span>
                    <span className="font-bold text-indigo-200">{globalPct}%</span>
                  </div>
                  <div className="mt-2 h-2.5 rounded-full bg-white/10 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-indigo-500 to-purple-500"
                      style={{ width: `${globalPct}%` }}
                    />
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setIsHistoryOpen(true)}
                  className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 border border-white/15 text-white text-sm font-bold transition"
                >
                  Historique
                </button>
                {!isEditingPMO ? (
                  <button
                    type="button"
                    onClick={() => setIsEditingPMO(true)}
                    className="px-4 py-2 rounded-lg bg-indigo-500/25 hover:bg-indigo-500/35 border border-indigo-500/40 text-white text-sm font-bold transition"
                  >
                    Modifier
                  </button>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        savePMOState();
                        setIsEditingPMO(false);
                      }}
                      className="px-4 py-2 rounded-lg bg-emerald-500/25 hover:bg-emerald-500/35 border border-emerald-500/40 text-white text-sm font-bold transition"
                    >
                      Sauvegarder
                    </button>
                    <button
                      type="button"
                      onClick={() => setIsEditingPMO(false)}
                      className="px-4 py-2 rounded-lg bg-white/10 hover:bg-white/15 border border-white/15 text-white text-sm font-bold transition"
                      title="Annuler (garde la dernière sauvegarde)"
                    >
                      Fermer
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Historique PMO */}
          {isHistoryOpen && (
            <div className="bg-slate-950/40 rounded-xl border border-indigo-500/25 p-5">
              <div className="flex items-center justify-between gap-3 mb-4">
                <div>
                  <div className="text-lg font-bold text-white">Historique des modifications</div>
                  <div className="text-xs text-gray-400">
                    Logs enregistrés lors de “Sauvegarder”.
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsHistoryOpen(false)}
                    className="px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 border border-white/15 text-white text-xs font-bold transition"
                  >
                    Fermer
                  </button>
                </div>
              </div>

              {pmoHistory.length === 0 ? (
                <div className="text-sm text-gray-400 py-6 text-center">Aucun log pour le moment.</div>
              ) : (
                <div className="max-h-[320px] overflow-y-auto divide-y divide-white/5">
                  {pmoHistory.map((h, idx) => (
                    <div key={`${h.ts}-${idx}`} className="py-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-indigo-200 truncate">{h.label}</div>
                          <div className="text-xs text-gray-400">
                        {new Date(h.ts).toLocaleString('fr-CA')} • {h.user || '—'} •{' '}
                        {h.kind === 'task_progress'
                          ? 'Avancement tâche'
                          : h.kind === 'task_meta'
                          ? 'Données tâche'
                          : 'Info trimestre'}
                          </div>
                        </div>
                        <div className="text-xs font-bold text-white whitespace-nowrap">
                          <span className="text-gray-300">{h.from}</span>
                          <span className="text-gray-500 mx-2">→</span>
                          <span className="text-emerald-200">{h.to}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="space-y-6">
            {roadmapOperationnel2026.map((quarter) => (
              <div key={quarter.trimestre} className="bg-slate-900/30 rounded-xl border border-indigo-500/20 p-6">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <h4 className="text-xl font-bold text-white flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-indigo-300" />
                      {quarter.trimestre} — {pmoQuarterOverrides[quarter.trimestre]?.theme ?? quarter.theme}
                    </h4>
                    {(pmoQuarterOverrides[quarter.trimestre]?.synthese ?? quarter.synthese) &&
                      (pmoQuarterOverrides[quarter.trimestre]?.synthese ?? quarter.synthese)!.length > 0 && (
                      <ul className="mt-2 text-sm text-gray-300 list-disc pl-6 space-y-1">
                        {(pmoQuarterOverrides[quarter.trimestre]?.synthese ?? quarter.synthese)!.map((s, idx) => (
                          <li key={idx}>{s}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {typeof (pmoQuarterOverrides[quarter.trimestre]?.totalHeures ?? quarter.totalHeures) === 'number' && (
                      <div className="bg-indigo-500/15 border border-indigo-500/30 rounded-lg px-4 py-2 text-center">
                        <div className="text-xs text-gray-400">Total estimé</div>
                        <div className="text-lg font-bold text-indigo-200">
                          ~{pmoQuarterOverrides[quarter.trimestre]?.totalHeures ?? quarter.totalHeures} h
                        </div>
                      </div>
                    )}
                    <div className="bg-white/10 border border-indigo-500/25 rounded-lg px-4 py-2 min-w-[120px]">
                      <div className="flex items-center justify-between text-xs text-gray-300">
                        <span className="font-semibold">Avancement</span>
                        <span className="font-bold text-indigo-200">{quarterStats[quarter.trimestre]?.pct ?? 0}%</span>
                      </div>
                      <div className="mt-2 h-2 rounded-full bg-white/10 overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-indigo-500 to-purple-500"
                          style={{ width: `${quarterStats[quarter.trimestre]?.pct ?? 0}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {isEditingPMO && (
                  <div className="mb-5 bg-white/5 rounded-lg border border-indigo-500/20 p-4">
                    <div className="text-sm font-bold text-indigo-200 mb-3">Modifier infos générales</div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div className="md:col-span-2">
                        <label className="block text-xs font-bold text-indigo-300 mb-1">Thème</label>
                        <input
                          value={pmoQuarterOverrides[quarter.trimestre]?.theme ?? quarter.theme}
                          onChange={(e) =>
                            setPmoQuarterOverrides((prev) => ({
                              ...prev,
                              [quarter.trimestre]: { ...(prev[quarter.trimestre] || {}), theme: e.target.value },
                            }))
                          }
                          className="w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-indigo-500/30 text-white text-sm focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-indigo-300 mb-1">Total estimé (heures)</label>
                        <input
                          type="number"
                          min={0}
                          value={pmoQuarterOverrides[quarter.trimestre]?.totalHeures ?? quarter.totalHeures ?? 0}
                          onChange={(e) =>
                            setPmoQuarterOverrides((prev) => ({
                              ...prev,
                              [quarter.trimestre]: {
                                ...(prev[quarter.trimestre] || {}),
                                totalHeures: Number(e.target.value || 0),
                              },
                            }))
                          }
                          className="w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-indigo-500/30 text-white text-sm focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                      <div className="md:col-span-3">
                        <label className="block text-xs font-bold text-indigo-300 mb-1">Synthèse (une par ligne)</label>
                        <textarea
                          rows={2}
                          value={(pmoQuarterOverrides[quarter.trimestre]?.synthese ?? quarter.synthese ?? []).join('\n')}
                          onChange={(e) =>
                            setPmoQuarterOverrides((prev) => ({
                              ...prev,
                              [quarter.trimestre]: {
                                ...(prev[quarter.trimestre] || {}),
                                synthese: e.target.value
                                  .split('\n')
                                  .map((s) => s.trim())
                                  .filter(Boolean),
                              },
                            }))
                          }
                          className="w-full px-3 py-2 rounded-lg bg-slate-900/60 border border-indigo-500/30 text-white text-sm focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  {quarter.axes.map((axe) => (
                    <div key={axe.id} className="bg-slate-950/30 rounded-lg border border-indigo-500/10 p-4">
                      <h5 className="text-lg font-bold text-indigo-200 mb-3">{axe.nom}</h5>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-indigo-500/20">
                              <th className="text-left py-2 px-3 text-xs font-bold text-indigo-300">Livrables</th>
                              <th className="text-center py-2 px-3 text-xs font-bold text-indigo-300 w-40">Responsables</th>
                              <th className="text-right py-2 px-3 text-xs font-bold text-indigo-300 w-24">Effort (h)</th>
                              <th className="text-left py-2 px-3 text-xs font-bold text-indigo-300 w-48">Jalon</th>
                              <th className="text-left py-2 px-3 text-xs font-bold text-indigo-300 w-64">Critère de validation</th>
                            </tr>
                          </thead>
                          <tbody>
                            {axe.tasks.map((t, idx) => (
                              <tr key={idx} className="border-b border-indigo-500/10 hover:bg-white/5 align-top">
                                {(() => {
                                  const k = getTaskKey(quarter.trimestre, axe.id, idx);
                                  const ov = pmoTaskOverrides[k] || {};
                                  const merged = { ...t, ...ov };
                                  const { texte: livrableTexte, responsables } = splitLivrableTextAndResponsables(merged.tache || '');
                                  const fallbackResponsables = [merged.r, merged.a]
                                    .filter(Boolean)
                                    .flatMap((value: string) => value.split('/'))
                                    .map((value: string) => normalizeResponsableName(value) || value.trim())
                                    .filter(Boolean);
                                  const responsablesAffiches = Array.from(new Set((responsables.length > 0 ? responsables : fallbackResponsables).filter(Boolean)));
                                  return (
                                    <>
                                      <td className="py-2 px-3 text-sm text-gray-200">
                                        {isEditingPMO ? (
                                          <textarea
                                            rows={2}
                                            value={merged.tache}
                                            onChange={(e) =>
                                              setPmoTaskOverrides((prev) => ({
                                                ...prev,
                                                [k]: { ...(prev[k] || {}), tache: e.target.value },
                                              }))
                                            }
                                            className="w-full px-2 py-1 rounded bg-slate-900/60 border border-indigo-500/30 text-white text-xs focus:ring-2 focus:ring-indigo-500"
                                          />
                                        ) : (
                                          livrableTexte
                                        )}
                                      </td>
                                      <td className="py-2 px-3 text-sm text-gray-300 text-center">
                                        {isEditingPMO ? (
                                          <div className="grid grid-cols-1 gap-2">
                                            <input
                                              value={merged.r}
                                              onChange={(e) =>
                                                setPmoTaskOverrides((prev) => ({
                                                  ...prev,
                                                  [k]: { ...(prev[k] || {}), r: e.target.value },
                                                }))
                                              }
                                              className="w-full px-2 py-1 rounded bg-slate-900/60 border border-indigo-500/30 text-white text-xs focus:ring-2 focus:ring-indigo-500"
                                              placeholder="R"
                                            />
                                            <input
                                              value={merged.a}
                                              onChange={(e) =>
                                                setPmoTaskOverrides((prev) => ({
                                                  ...prev,
                                                  [k]: { ...(prev[k] || {}), a: e.target.value },
                                                }))
                                              }
                                              className="w-full px-2 py-1 rounded bg-slate-900/60 border border-indigo-500/30 text-white text-xs focus:ring-2 focus:ring-indigo-500"
                                              placeholder="A"
                                            />
                                          </div>
                                        ) : responsablesAffiches.length > 0 ? (
                                          <div className="flex items-center justify-center gap-1.5 flex-wrap">
                                            {responsablesAffiches.map((responsable, rIdx) => {
                                              const avatarSrc = avatarMap[responsable];
                                              return (
                                                <div key={rIdx} className="flex items-center gap-1" title={responsable}>
                                                  {avatarSrc ? (
                                                    avatarSrc === '$' || avatarSrc === '👤' ? (
                                                      <div className="w-6 h-6 rounded-full bg-indigo-500/20 border border-indigo-500/50 flex items-center justify-center text-xs font-bold text-indigo-300">
                                                        {avatarSrc}
                                                      </div>
                                                    ) : (
                                                      <img
                                                        src={avatarSrc}
                                                        alt={responsable}
                                                        className="w-6 h-6 rounded-full border-2 border-indigo-500/50 object-cover"
                                                        onError={(e) => {
                                                          const target = e.target as HTMLImageElement;
                                                          target.style.display = 'none';
                                                        }}
                                                      />
                                                    )
                                                  ) : (
                                                    <div className="w-6 h-6 rounded-full bg-indigo-500/20 border-2 border-indigo-500/50 flex items-center justify-center text-xs text-white">
                                                      {responsable.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                                                    </div>
                                                  )}
                                                </div>
                                              );
                                            })}
                                          </div>
                                        ) : (
                                          <span className="text-xs text-gray-500">-</span>
                                        )}
                                      </td>
                                      <td className="py-2 px-3 text-right text-sm font-semibold text-indigo-200">
                                        {isEditingPMO ? (
                                          <input
                                            type="number"
                                            min={0}
                                            value={Number(merged.heures || 0)}
                                            onChange={(e) => {
                                              const v = Number(e.target.value || 0);
                                              setPmoTaskOverrides((prev) => ({
                                                ...prev,
                                                [k]: { ...(prev[k] || {}), heures: Number.isFinite(v) ? v : 0 },
                                              }));
                                            }}
                                            className="w-20 text-right px-2 py-1 rounded bg-slate-900/60 border border-indigo-500/30 text-white text-xs focus:ring-2 focus:ring-indigo-500"
                                          />
                                        ) : merged.heures > 0 ? (
                                          `${merged.heures}h`
                                        ) : (
                                          <span className="text-xs text-gray-500">-</span>
                                        )}
                                      </td>
                                    </>
                                  );
                                })()}
                                <td className="py-2 px-3 text-sm text-gray-300">
                                  <div className="flex items-center justify-between gap-2">
                                    {(() => {
                                      const k = getTaskKey(quarter.trimestre, axe.id, idx);
                                      const ov = pmoTaskOverrides[k] || {};
                                      const jalon = ov.jalon ?? t.jalon;
                                      return isEditingPMO ? (
                                        <input
                                          value={jalon}
                                          onChange={(e) =>
                                            setPmoTaskOverrides((prev) => ({
                                              ...prev,
                                              [k]: { ...(prev[k] || {}), jalon: e.target.value },
                                            }))
                                          }
                                          className="w-full px-2 py-1 rounded bg-slate-900/60 border border-indigo-500/30 text-white text-xs focus:ring-2 focus:ring-indigo-500"
                                        />
                                      ) : (
                                        <div className="font-medium">{jalon}</div>
                                      );
                                    })()}
                                    <div className="text-xs font-bold text-indigo-200">
                                      {Math.max(0, Math.min(100, pmoProgress[getTaskKey(quarter.trimestre, axe.id, idx)] ?? 0))}%
                                    </div>
                                  </div>
                                  <div className="mt-2 h-2 rounded-full bg-white/10 overflow-hidden">
                                    <div
                                      className="h-full bg-gradient-to-r from-indigo-500 to-purple-500"
                                      style={{
                                        width: `${Math.max(
                                          0,
                                          Math.min(100, pmoProgress[getTaskKey(quarter.trimestre, axe.id, idx)] ?? 0)
                                        )}%`,
                                      }}
                                    />
                                  </div>
                                  {isEditingPMO && (
                                    <div className="mt-2 flex items-center gap-2">
                                      <input
                                        type="number"
                                        min={0}
                                        max={100}
                                        value={pmoProgress[getTaskKey(quarter.trimestre, axe.id, idx)] ?? 0}
                                        onChange={(e) => {
                                          const v = Math.max(0, Math.min(100, Number(e.target.value || 0)));
                                          setPmoProgress((prev) => ({
                                            ...prev,
                                            [getTaskKey(quarter.trimestre, axe.id, idx)]: v,
                                          }));
                                        }}
                                        className="w-20 px-2 py-1 rounded bg-slate-900/60 border border-indigo-500/30 text-white text-xs focus:ring-2 focus:ring-indigo-500"
                                      />
                                      <span className="text-xs text-gray-400">%</span>
                                    </div>
                                  )}
                                </td>
                                <td className="py-2 px-3 text-sm text-gray-300">
                                  {(() => {
                                    const k = getTaskKey(quarter.trimestre, axe.id, idx);
                                    const ov = pmoTaskOverrides[k] || {};
                                    const critere = ov.critere ?? t.critere;
                                    return isEditingPMO ? (
                                      <textarea
                                        rows={2}
                                        value={critere}
                                        onChange={(e) =>
                                          setPmoTaskOverrides((prev) => ({
                                            ...prev,
                                            [k]: { ...(prev[k] || {}), critere: e.target.value },
                                          }))
                                        }
                                        className="w-full px-2 py-1 rounded bg-slate-900/60 border border-indigo-500/30 text-white text-xs focus:ring-2 focus:ring-indigo-500"
                                      />
                                    ) : (
                                      critere
                                    );
                                  })()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {roadmapMode === 'strategie' && (
        <div className="space-y-6">
          {/* Résumé effort par personne */}
          {(() => {
        // Fonction pour obtenir le % d'avancement d'un livrable
        const getLivrableProgress = (texte: string): number => {
          const livrablesCompletes100 = [
            'Déploiement PRIsME Employé pour 50% des employés',
            'Cloner TactiCE (Projet) dans PRIsME avec structure complète',
            'Gouvernance projet structurée (rôles, décisions, escalade) - pilotée par DB et EH',
            'Onboarding intégré à PRIsME Employé avec checklist par rôle',
            'RACI complet documenté et visible dans PRIsME'
          ];
          const livrablesCompletes75 = [
            'Gestion documentaire 8 1/2 x 11 impeccable (PDF export optimisé)',
            'Automatisation génération documents (rapports, livrables)',
            'Tous les rôles ont leur PRIsME Employé complet et à jour',
            'Processus livraison PRIsME Client optimisé (workflow, checkpoints)',
            'Documentation processus par rôle (responsabilités, livrables, outils)',
            'Lien avec Shield / Decision Desk opérationnel (firewall mental)',
            'Format Decision Packet standardisé pour escalade vers Dany',
            'Rituels gouvernance documentés (2 réunions/mois, présence Dany 1x)',
            'Processus décision formalisé (critères, escalade, délais)'
          ];
          const livrablesCompletes50 = [
            'Formation gestionnaires sur leur responsabilité contenu départemental',
            'Gestionnaires autonomes sur leur contenu départemental',
            'Mise à jour régulière PRIsME Employé (mensuelle minimum)',
            'Feedback boucle intégrée (suggestions, améliorations)',
            'Intégration PRIsME Employé avec outils quotidiens (SSO, notifications)',
            'Intégration projets TactiCE avec PRIsME Client (livraison traçable)',
            'BOT PRIsME opérationnel (réponses FAQ, guidage processus)',
            'Interface conversationnelle pour guider les Primes',
            'Sécurité intégrée (classification, accès, audit)',
            'Qualité 100% vérifiée avant livraison (checklist, validation)',
            'Templates livraison PRIsME Client standardisés (structure, contenu)',
            'Pages PRIsME Employé versionnées et mises à jour régulièrement',
            'Métriques adoption PRIsME Employé (visites, temps onboarding, autonomie)',
            'Règles d\'escalade claires (quand escalader, quand non)'
          ];
          const livrablesCompletes25 = [
            'Automatisation tâches répétitives (création pages, notifications, rappels)',
            'Amélioration continue processus (rétrospectives, ajustements)'
          ];
          if (livrablesCompletes100.includes(texte)) return 100;
          if (livrablesCompletes75.includes(texte)) return 75;
          if (livrablesCompletes50.includes(texte)) return 50;
          if (livrablesCompletes25.includes(texte)) return 25;
          return 0;
        };

        // Calculer le total d'heures par personne et le % d'avancement
        const effortParPersonne: Record<string, number> = {};
        const progressParPersonne: Record<string, { total: number; progress: number }> = {};
        
        roadmap2026.forEach(quarter => {
          quarter.axes.forEach(axe => {
            // Récupérer les personnes A et R pour cet axe
            const mapping: Record<string, string> = {
              'Livraison & Exécution': 'Charge projet & Client',
              'PRIsME Employé et Département': 'RH & Équipe',
              'Technologie & Outils (BOT)': 'Innovation & IA',
              'Gouvernance & Décisions': 'Gouvernance & rôles',
              'Maturité globale': 'Qualité, KPI & Suivi',
              'Innovation & Évolution': 'Innovation & IA'
            };
            const nomRACI = mapping[axe.axe] || axe.axe;
            const activite = raciData.find(a => (a as { pillier?: string }).pillier === nomRACI);
            
            if (activite) {
              const personnesAR: string[] = [];
              Object.entries(activite.roles).forEach(([nom, role]) => {
                if ((role === 'A' || role === 'R') && !['Comité PRIsME', 'Primes', 'Directeurs de compte', 'Gestionnaire', 'Programmeur'].includes(nom)) {
                  personnesAR.push(nom);
                }
              });
              
              // Créer une copie des rôles pour les calculs
              const rolesPourCalcul: Record<string, string> = {};
              Object.entries(activite.roles).forEach(([nom, role]) => {
                if (role && typeof role === 'string') {
                  rolesPourCalcul[nom] = role;
                }
              });
              
              // Vérifier s'il y a déjà un A dans la liste (après avoir créé rolesPourCalcul)
              const hasA = personnesAR.some(nom => rolesPourCalcul[nom] === 'A');
              
              // Pour Livraison & Exécution, ajouter Dany seulement s'il n'y a pas déjà d'A
              if (nomRACI === 'Charge projet & Client' && !hasA && !personnesAR.includes('Dany Bourassa')) {
                personnesAR.push('Dany Bourassa');
                rolesPourCalcul['Dany Bourassa'] = 'A';
              }
              
              // Répartir l'effort : A prend 60%, R partagent les 40% (divisé par nombre de R)
              const personnesA = personnesAR.filter(nom => rolesPourCalcul[nom] === 'A');
              const personnesR = personnesAR.filter(nom => rolesPourCalcul[nom] === 'R');
              
              axe.livrables.forEach((livrable: any) => {
                const texte = typeof livrable === 'string' ? livrable : livrable.texte;
                const effort = typeof livrable === 'string' ? 0 : livrable.effort || 0;
                const livrableProgress = getLivrableProgress(texte);
                
                if (effort > 0 && personnesAR.length > 0) {
                  // Répartition : A = 60% / nombre de A, R = 40% / nombre de R
                  const effortA = personnesA.length > 0 ? (effort * 0.6) / personnesA.length : 0;
                  const effortR = personnesR.length > 0 ? (effort * 0.4) / personnesR.length : 0;
                  
                  personnesA.forEach(nom => {
                    effortParPersonne[nom] = (effortParPersonne[nom] || 0) + effortA;
                    if (!progressParPersonne[nom]) {
                      progressParPersonne[nom] = { total: 0, progress: 0 };
                    }
                    progressParPersonne[nom].total += effortA;
                    progressParPersonne[nom].progress += effortA * (livrableProgress / 100);
                  });
                  personnesR.forEach(nom => {
                    effortParPersonne[nom] = (effortParPersonne[nom] || 0) + effortR;
                    if (!progressParPersonne[nom]) {
                      progressParPersonne[nom] = { total: 0, progress: 0 };
                    }
                    progressParPersonne[nom].total += effortR;
                    progressParPersonne[nom].progress += effortR * (livrableProgress / 100);
                  });
                }
              });
            }
          });
        });
        
        // Calculer le % d'avancement final pour chaque personne
        const progressFinalParPersonne: Record<string, number> = {};
        Object.entries(progressParPersonne).forEach(([nom, data]) => {
          if (data.total > 0) {
            progressFinalParPersonne[nom] = Math.round((data.progress / data.total) * 100);
          } else {
            progressFinalParPersonne[nom] = 0;
          }
        });
        
        const personnesTriees = Object.entries(effortParPersonne)
          .sort((a, b) => b[1] - a[1])
          .filter(([_, effort]) => effort > 0);
        
        return (
          <div className="bg-gradient-to-br from-slate-800/50 to-slate-900/50 rounded-xl p-6 border-2 border-indigo-500/30 mb-6">
            <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Target className="w-6 h-6 text-indigo-400" />
              Répartition effort 2026 par personne
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {personnesTriees.map(([nom, effort]) => {
                const avatarSrc = avatarMap[nom];
                const progressPersonne = progressFinalParPersonne[nom] || 0;
                return (
                  <div key={nom} className="bg-slate-900/50 rounded-lg p-3 border border-indigo-500/20">
                    <div className="flex items-center gap-2 mb-3">
                      {avatarSrc ? (
                        avatarSrc === '$' || avatarSrc === '👤' ? (
                          <div className="w-8 h-8 rounded-full bg-indigo-500/20 border border-indigo-500/50 flex items-center justify-center text-xs font-bold text-indigo-300 cursor-help" title={nom}>
                            {avatarSrc}
                          </div>
                        ) : (
                          <img 
                            src={avatarSrc} 
                            alt={nom} 
                            title={nom}
                            className="w-8 h-8 rounded-full border-2 border-indigo-500/50 object-cover cursor-help"
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                            }}
                          />
                        )
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-indigo-500/20 border-2 border-indigo-500/50 flex items-center justify-center text-xs text-white cursor-help" title={nom}>
                          {nom.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-300 truncate">{nom.split(' ')[0]}</p>
                        <p className="text-xs text-gray-400 truncate">{nom.split(' ').slice(1).join(' ')}</p>
                      </div>
                    </div>
                    <div className="flex flex-col items-center gap-2">
                      {/* Cercle de progression */}
                      <div className="w-16 h-16 rounded-full bg-emerald-500/20 border-2 border-emerald-500/50 flex items-center justify-center relative">
                        <svg className="w-12 h-12 transform -rotate-90" viewBox="0 0 24 24">
                          <circle
                            cx="12"
                            cy="12"
                            r="10"
                            fill="none"
                            stroke="rgba(16, 185, 129, 0.2)"
                            strokeWidth="2"
                          />
                          <circle
                            cx="12"
                            cy="12"
                            r="10"
                            fill="none"
                            stroke="rgb(16, 185, 129)"
                            strokeWidth="2"
                            strokeDasharray={`${2 * Math.PI * 10 * (progressPersonne / 100)} ${2 * Math.PI * 10}`}
                            strokeLinecap="round"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-xs font-bold text-emerald-300">{progressPersonne}%</span>
                        </div>
                      </div>
                      <div className="text-center">
                        <p className="text-xl font-bold text-indigo-300">{Math.round(effort)}</p>
                        <p className="text-xs text-gray-400">heures</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-4 border-t border-indigo-500/20">
              <p className="text-sm text-gray-400">
                <strong className="text-white">Total : {Math.round(Object.values(effortParPersonne).reduce((a, b) => a + b, 0))} heures</strong> réparties sur l'année 2026
              </p>
            </div>
          </div>
        );
          })()}

      <div className="space-y-6">
        {roadmap2026.map((quarter, qIdx) => {
          const colorMap = {
            'Q1 2026': 'from-indigo-500/20 to-indigo-600/20 border-indigo-500/30',
            'Q2 2026': 'from-purple-500/20 to-purple-600/20 border-purple-500/30',
            'Q3 2026': 'from-blue-500/20 to-blue-600/20 border-blue-500/30',
            'Q4 2026': 'from-emerald-500/20 to-emerald-600/20 border-emerald-500/30'
          };
          const bgClass = colorMap[quarter.trimestre as keyof typeof colorMap];

          // Fonction pour calculer le % d'avancement d'un livrable
          const getLivrableProgress = (texte: string): number => {
            const livrablesCompletes100 = [
              'Déploiement PRIsME Employé pour 50% des employés',
              'Cloner TactiCE (Projet) dans PRIsME avec structure complète',
              'Gouvernance projet structurée (rôles, décisions, escalade) - pilotée par DB et EH',
              'Onboarding intégré à PRIsME Employé avec checklist par rôle',
              'RACI complet documenté et visible dans PRIsME'
            ];
            const livrablesCompletes75 = [
              'Gestion documentaire 8 1/2 x 11 impeccable (PDF export optimisé)',
              'Automatisation génération documents (rapports, livrables)',
              'Tous les rôles ont leur PRIsME Employé complet et à jour',
              'Processus livraison PRIsME Client optimisé (workflow, checkpoints)',
              'Documentation processus par rôle (responsabilités, livrables, outils)',
              'Lien avec Shield / Decision Desk opérationnel (firewall mental)',
              'Format Decision Packet standardisé pour escalade vers Dany',
              'Rituels gouvernance documentés (2 réunions/mois, présence Dany 1x)',
              'Processus décision formalisé (critères, escalade, délais)'
            ];
            const livrablesCompletes50 = [
              'Formation gestionnaires sur leur responsabilité contenu départemental',
              'Gestionnaires autonomes sur leur contenu départemental',
              'Mise à jour régulière PRIsME Employé (mensuelle minimum)',
              'Feedback boucle intégrée (suggestions, améliorations)',
              'Intégration PRIsME Employé avec outils quotidiens (SSO, notifications)',
              'Intégration projets TactiCE avec PRIsME Client (livraison traçable)',
              'BOT PRIsME opérationnel (réponses FAQ, guidage processus)',
              'Interface conversationnelle pour guider les Primes',
              'Sécurité intégrée (classification, accès, audit)',
              'Qualité 100% vérifiée avant livraison (checklist, validation)',
              'Templates livraison PRIsME Client standardisés (structure, contenu)',
              'Pages PRIsME Employé versionnées et mises à jour régulièrement',
              'Métriques adoption PRIsME Employé (visites, temps onboarding, autonomie)',
              'Règles d\'escalade claires (quand escalader, quand non)'
            ];
          const livrablesCompletes25 = [
            'Automatisation tâches répétitives (création pages, notifications, rappels)',
            'Amélioration continue processus (rétrospectives, ajustements)'
          ];
            if (livrablesCompletes100.includes(texte)) return 100;
            if (livrablesCompletes75.includes(texte)) return 75;
            if (livrablesCompletes50.includes(texte)) return 50;
            if (livrablesCompletes25.includes(texte)) return 25;
            return 0;
          };

          // Calculer le % d'avancement par axe
          const calculateAxeProgress = (axe: any): number => {
            if (!axe.livrables || axe.livrables.length === 0) return 0;
            let totalProgress = 0;
            let count = 0;
            axe.livrables.forEach((livrable: any) => {
              const texte = typeof livrable === 'string' ? livrable : livrable.texte;
              totalProgress += getLivrableProgress(texte);
              count++;
            });
            return count > 0 ? Math.round(totalProgress / count) : 0;
          };

          // Calculer le % d'avancement du trimestre (moyenne des axes)
          const calculateQuarterProgress = (): number => {
            if (!quarter.axes || quarter.axes.length === 0) return 0;
            let totalProgress = 0;
            let count = 0;
            quarter.axes.forEach((axe: any) => {
              const axeProgress = calculateAxeProgress(axe);
              totalProgress += axeProgress;
              count++;
            });
            return count > 0 ? Math.round(totalProgress / count) : 0;
          };

          const quarterProgress = calculateQuarterProgress();

          return (
            <div key={qIdx} className={`bg-gradient-to-br ${bgClass} rounded-xl p-6 border-2`}>
              <div className="mb-4">
                <div className="flex items-center justify-between gap-4">
                  <div className="flex-1">
                    <h4 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
                      <Calendar className="w-6 h-6" />
                      {quarter.trimestre}
                    </h4>
                    {quarter.intention && (
                      <p className="text-sm text-gray-300 italic ml-8">{quarter.intention}</p>
                    )}
                  </div>
                  {/* Cercle de progression pour le trimestre */}
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="text-xs text-gray-400 mb-1">Avancement</div>
                      <div className="text-lg font-bold text-white">{quarterProgress}%</div>
                    </div>
                    <div className="w-16 h-16 rounded-full bg-emerald-500/20 border-2 border-emerald-500/50 flex items-center justify-center relative">
                      <svg className="w-12 h-12 transform -rotate-90" viewBox="0 0 24 24">
                        <circle
                          cx="12"
                          cy="12"
                          r="10"
                          fill="none"
                          stroke="rgba(16, 185, 129, 0.2)"
                          strokeWidth="2"
                        />
                        <circle
                          cx="12"
                          cy="12"
                          r="10"
                          fill="none"
                          stroke="rgb(16, 185, 129)"
                          strokeWidth="2"
                          strokeDasharray={`${2 * Math.PI * 10 * (quarterProgress / 100)} ${2 * Math.PI * 10}`}
                          strokeLinecap="round"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-4">
                {quarter.axes.map((axe, aIdx) => {
                  // Récupérer les personnes A et R pour cet axe selon le RACI
                  const getPersonnesAR = (axeName: string) => {
                    // Mapping entre noms de la roadmap et noms du RACI
                    const mapping: Record<string, string> = {
                      'Livraison & Exécution': 'Charge projet & Client',
                      'PRIsME Employé et Département': 'RH & Équipe',
                      'Technologie & Outils (BOT)': 'Innovation & IA',
                      'Gouvernance & Décisions': 'Gouvernance & rôles',
                      'Maturité globale': 'Qualité, KPI & Suivi',
                      'Innovation & Évolution': 'Innovation & IA'
                    };
                    const nomRACI = mapping[axeName] || axeName;
                    const activite = raciData.find(a => (a as { pillier?: string }).pillier === nomRACI);
                    if (!activite) return [];
                    
                    const personnes: Array<{ nom: string; role: 'A' | 'R' | 'C' }> = [];
                    Object.entries(activite.roles).forEach(([nom, role]) => {
                      // Pour PRIsME Employé et Département, inclure aussi Dany (C)
                      if (nomRACI === 'PRIsME Employé et Département' && nom === 'Dany Bourassa') {
                        personnes.push({ nom, role: 'C' as 'A' | 'R' | 'C' });
                      } else if ((role === 'A' || role === 'R') && !['Comité PRIsME', 'Primes', 'Directeurs de compte', 'Gestionnaire', 'Programmeur'].includes(nom)) {
                        personnes.push({ nom, role: role as 'A' | 'R' | 'C' });
                      }
                    });
                    
                    // Vérifier s'il y a déjà un A dans la liste
                    const hasA = personnes.some(p => p.role === 'A');
                    
                    // Pour Livraison & Exécution, ajouter Dany Bourassa comme A seulement s'il n'y a pas déjà d'A
                    if (axeName === 'Livraison & Exécution' && !hasA && !personnes.find(p => p.nom === 'Dany Bourassa')) {
                      personnes.push({ nom: 'Dany Bourassa', role: 'A' });
                    }
                    return personnes;
                  };
                  
                  const personnesAR = getPersonnesAR(axe.axe);
                  
                  // Calculer le total d'heures pour cet axe
                  const totalEffortAxe = axe.livrables.reduce((total: number, livrable: any) => {
                    const effort = typeof livrable === 'string' ? 0 : livrable.effort || 0;
                    return total + effort;
                  }, 0);
                  
                  // Calculer le % d'avancement de l'axe
                  const axeProgress = calculateAxeProgress(axe);
                  
                  return (
                    <div key={aIdx} className="bg-slate-900/50 rounded-lg p-4 border border-indigo-500/20">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h5 className="text-lg font-bold text-indigo-300 mb-1">{axe.axe}</h5>
                          {axe.intention && (
                            <p className="text-xs text-gray-400 italic mb-2">{axe.intention}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-3 flex-shrink-0 ml-3">
                          {/* Cercle de progression pour l'axe */}
                          <div className="flex items-center gap-2">
                            <div className="text-right">
                              <div className="text-xs text-gray-400">Avancement</div>
                              <div className="text-sm font-bold text-indigo-300">{axeProgress}%</div>
                            </div>
                            <div className="w-12 h-12 rounded-full bg-emerald-500/20 border-2 border-emerald-500/50 flex items-center justify-center relative">
                              <svg className="w-9 h-9 transform -rotate-90" viewBox="0 0 24 24">
                                <circle
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  fill="none"
                                  stroke="rgba(16, 185, 129, 0.2)"
                                  strokeWidth="2"
                                />
                                <circle
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  fill="none"
                                  stroke="rgb(16, 185, 129)"
                                  strokeWidth="2"
                                  strokeDasharray={`${2 * Math.PI * 10 * (axeProgress / 100)} ${2 * Math.PI * 10}`}
                                  strokeLinecap="round"
                                />
                              </svg>
                            </div>
                          </div>
                          {totalEffortAxe > 0 && (
                            <div className="bg-indigo-500/20 border border-indigo-500/50 rounded-lg px-3 py-1.5">
                              <p className="text-xs text-gray-400 text-center">Total</p>
                              <p className="text-lg font-bold text-indigo-300 text-center">{totalEffortAxe}h</p>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-indigo-500/20">
                              <th className="text-left py-2 px-3 text-xs font-bold text-indigo-300">Livrables</th>
                              <th className="text-center py-2 px-3 text-xs font-bold text-indigo-300 w-32">Responsables</th>
                              <th className="text-center py-2 px-3 text-xs font-bold text-indigo-300 w-24">Priorité</th>
                              <th className="text-right py-2 px-3 text-xs font-bold text-indigo-300 w-24">Effort (h)</th>
                              <th className="text-center py-2 px-3 text-xs font-bold text-indigo-300 w-20">Complété</th>
                            </tr>
                          </thead>
                          <tbody>
                            {axe.livrables.map((livrable: any, lIdx: number) => {
                              const rawTexte = typeof livrable === 'string' ? livrable : livrable.texte;
                              const { texte, responsables: responsablesFromText } = splitLivrableTextAndResponsables(rawTexte);
                              const effort = typeof livrable === 'string' ? 0 : livrable.effort || 0;
                              const priorite = typeof livrable === 'string' ? 'Moyenne' : livrable.priorite || 'Moyenne';
                              const responsablesAffiches = responsablesFromText.length > 0
                                ? responsablesFromText.map((nom) => ({ nom, role: 'R' as 'A' | 'R' | 'C' }))
                                : personnesAR;
                              
                              // Liste des livrables complétés à 100%
                              const livrablesCompletes100 = [
                                'Déploiement PRIsME Employé pour 50% des employés',
                                'Cloner TactiCE (Projet) dans PRIsME avec structure complète',
                                'Gouvernance projet structurée (rôles, décisions, escalade) - pilotée par DB et EH',
                                'Onboarding intégré à PRIsME Employé avec checklist par rôle',
                                'RACI complet documenté et visible dans PRIsME'
                              ];
                              // Liste des livrables complétés à 75%
            const livrablesCompletes75 = [
              'Gestion documentaire 8 1/2 x 11 impeccable (PDF export optimisé)',
              'Automatisation génération documents (rapports, livrables)',
              'Tous les rôles ont leur PRIsME Employé complet et à jour',
              'Processus livraison PRIsME Client optimisé (workflow, checkpoints)',
              'Documentation processus par rôle (responsabilités, livrables, outils)',
              'Lien avec Shield / Decision Desk opérationnel (firewall mental)',
              'Format Decision Packet standardisé pour escalade vers Dany',
              'Rituels gouvernance documentés (2 réunions/mois, présence Dany 1x)',
              'Processus décision formalisé (critères, escalade, délais)'
            ];
                              // Liste des livrables complétés à 50%
                              const livrablesCompletes50 = [
                                'Formation gestionnaires sur leur responsabilité contenu départemental',
                                'Gestionnaires autonomes sur leur contenu départemental',
                                'Mise à jour régulière PRIsME Employé (mensuelle minimum)',
                                'Feedback boucle intégrée (suggestions, améliorations)',
                                'Intégration PRIsME Employé avec outils quotidiens (SSO, notifications)',
                                'Intégration projets TactiCE avec PRIsME Client (livraison traçable)',
                                'BOT PRIsME opérationnel (réponses FAQ, guidage processus)',
                                'Interface conversationnelle pour guider les Primes',
                                'Sécurité intégrée (classification, accès, audit)',
                                'Qualité 100% vérifiée avant livraison (checklist, validation)',
                                'Templates livraison PRIsME Client standardisés (structure, contenu)',
                                'Pages PRIsME Employé versionnées et mises à jour régulièrement',
                                'Métriques adoption PRIsME Employé (visites, temps onboarding, autonomie)',
                                'Règles d\'escalade claires (quand escalader, quand non)'
                              ];
                              // Liste des livrables complétés à 25%
                              const livrablesCompletes25 = [
                                'Automatisation tâches répétitives (création pages, notifications, rappels)',
                                'Amélioration continue processus (rétrospectives, ajustements)'
                              ];
                              const estComplete100 = livrablesCompletes100.includes(texte);
                              const estComplete75 = livrablesCompletes75.includes(texte);
                              const estComplete50 = livrablesCompletes50.includes(texte);
                              const estComplete25 = livrablesCompletes25.includes(texte);
                              
                              const prioriteColors: Record<string, string> = {
                                'Haute': 'bg-red-500/20 text-red-300 border-red-500/50',
                                'Moyenne': 'bg-yellow-500/20 text-yellow-300 border-yellow-500/50',
                                'Basse': 'bg-green-500/20 text-green-300 border-green-500/50'
                              };
                              
                              return (
                                <tr key={lIdx} className="border-b border-indigo-500/10 hover:bg-white/5">
                                  <td className="py-2 px-3">
                                    <div className="flex items-start gap-2 text-gray-300 text-sm">
                                      <CheckCircle className="w-4 h-4 text-indigo-400 flex-shrink-0 mt-0.5" />
                                      <span>{texte}</span>
                                    </div>
                                  </td>
                                  <td className="py-2 px-3 text-center">
                                    {responsablesAffiches.length > 0 ? (
                                      <div className="flex items-center justify-center gap-1.5 flex-wrap">
                                        {responsablesAffiches.map((personne, pIdx) => {
                                          const avatarSrc = avatarMap[personne.nom];
                                          return (
                                            <div key={pIdx} className="flex items-center gap-1" title={personne.nom}>
                                              {avatarSrc ? (
                                                avatarSrc === '$' || avatarSrc === '👤' ? (
                                                  <div className="w-6 h-6 rounded-full bg-indigo-500/20 border border-indigo-500/50 flex items-center justify-center text-xs font-bold text-indigo-300" title={personne.nom}>
                                                    {avatarSrc}
                                                  </div>
                                                ) : (
                                                  <img 
                                                    src={avatarSrc} 
                                                    alt={personne.nom} 
                                                    title={personne.nom}
                                                    className="w-6 h-6 rounded-full border-2 border-indigo-500/50 object-cover cursor-help"
                                                    onError={(e) => {
                                                      const target = e.target as HTMLImageElement;
                                                      target.style.display = 'none';
                                                    }}
                                                  />
                                                )
                                              ) : (
                                                <div className="w-6 h-6 rounded-full bg-indigo-500/20 border-2 border-indigo-500/50 flex items-center justify-center text-xs text-white cursor-help" title={personne.nom}>
                                                  {personne.nom.split(' ').map(n => n[0]).join('').slice(0, 2)}
                                                </div>
                                              )}
                                              <span className={`text-xs font-bold px-1 py-0.5 rounded cursor-help ${
                                                personne.role === 'A' 
                                                  ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/50' 
                                                  : personne.role === 'R'
                                                  ? 'bg-blue-500/20 text-blue-300 border border-blue-500/50'
                                                  : 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/50'
                                              }`} title={`${personne.nom} - ${personne.role === 'A' ? 'Accountable' : personne.role === 'R' ? 'Responsable' : 'Consulté'}`}>
                                                {personne.role}
                                              </span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    ) : (
                                      <span className="text-xs text-gray-500">-</span>
                                    )}
                                  </td>
                                  <td className="py-2 px-3 text-center">
                                    <span className={`text-xs font-bold px-2 py-1 rounded border ${prioriteColors[priorite] || prioriteColors['Moyenne']}`}>
                                      {priorite}
                                    </span>
                                  </td>
                                  <td className="py-2 px-3 text-right">
                                    {effort > 0 ? (
                                      <span className="text-sm font-semibold text-indigo-300">{effort}h</span>
                                    ) : (
                                      <span className="text-xs text-gray-500">-</span>
                                    )}
                                  </td>
                                  <td className="py-2 px-3 text-center">
                                    {estComplete100 ? (
                                      <div className="flex items-center justify-center">
                                        <div className="w-8 h-8 rounded-full bg-emerald-500/20 border-2 border-emerald-500/50 flex items-center justify-center">
                                          <CheckCircle className="w-5 h-5 text-emerald-400" />
                                        </div>
                                      </div>
                                    ) : estComplete75 ? (
                                      <div className="flex items-center justify-center">
                                        <div className="w-8 h-8 rounded-full bg-emerald-500/20 border-2 border-emerald-500/50 flex items-center justify-center relative">
                                          {/* Cercle de progression à 75% */}
                                          <svg className="w-6 h-6 transform -rotate-90" viewBox="0 0 24 24">
                                            {/* Cercle de fond */}
                                            <circle
                                              cx="12"
                                              cy="12"
                                              r="10"
                                              fill="none"
                                              stroke="rgba(16, 185, 129, 0.2)"
                                              strokeWidth="2"
                                            />
                                            {/* Arc de progression à 75% */}
                                            <circle
                                              cx="12"
                                              cy="12"
                                              r="10"
                                              fill="none"
                                              stroke="rgb(16, 185, 129)"
                                              strokeWidth="2"
                                              strokeDasharray={`${2 * Math.PI * 10 * 0.75} ${2 * Math.PI * 10}`}
                                              strokeLinecap="round"
                                            />
                                          </svg>
                                        </div>
                                      </div>
                                    ) : estComplete50 ? (
                                      <div className="flex items-center justify-center">
                                        <div className="w-8 h-8 rounded-full bg-emerald-500/20 border-2 border-emerald-500/50 flex items-center justify-center relative">
                                          {/* Cercle de progression à 50% */}
                                          <svg className="w-6 h-6 transform -rotate-90" viewBox="0 0 24 24">
                                            {/* Cercle de fond */}
                                            <circle
                                              cx="12"
                                              cy="12"
                                              r="10"
                                              fill="none"
                                              stroke="rgba(16, 185, 129, 0.2)"
                                              strokeWidth="2"
                                            />
                                            {/* Arc de progression à 50% */}
                                            <circle
                                              cx="12"
                                              cy="12"
                                              r="10"
                                              fill="none"
                                              stroke="rgb(16, 185, 129)"
                                              strokeWidth="2"
                                              strokeDasharray={`${2 * Math.PI * 10 * 0.5} ${2 * Math.PI * 10}`}
                                              strokeLinecap="round"
                                            />
                                          </svg>
                                        </div>
                                      </div>
                                    ) : estComplete25 ? (
                                      <div className="flex items-center justify-center">
                                        <div className="w-8 h-8 rounded-full bg-emerald-500/20 border-2 border-emerald-500/50 flex items-center justify-center relative">
                                          {/* Cercle de progression à 25% */}
                                          <svg className="w-6 h-6 transform -rotate-90" viewBox="0 0 24 24">
                                            {/* Cercle de fond */}
                                            <circle
                                              cx="12"
                                              cy="12"
                                              r="10"
                                              fill="none"
                                              stroke="rgba(16, 185, 129, 0.2)"
                                              strokeWidth="2"
                                            />
                                            {/* Arc de progression à 25% */}
                                            <circle
                                              cx="12"
                                              cy="12"
                                              r="10"
                                              fill="none"
                                              stroke="rgb(16, 185, 129)"
                                              strokeWidth="2"
                                              strokeDasharray={`${2 * Math.PI * 10 * 0.25} ${2 * Math.PI * 10}`}
                                              strokeLinecap="round"
                                            />
                                          </svg>
                                        </div>
                                      </div>
                                    ) : (
                                      <span className="text-xs text-gray-500">-</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          {totalEffortAxe > 0 && (
                            <tfoot>
                              <tr className="border-t-2 border-indigo-500/30 bg-indigo-500/10">
                                <td className="py-2 px-3 text-right font-bold text-indigo-300" colSpan={4}>
                                  Total {axe.axe}
                                </td>
                                <td className="py-2 px-3 text-right">
                                  <span className="text-lg font-bold text-indigo-300">{totalEffortAxe}h</span>
                                </td>
                              </tr>
                            </tfoot>
                          )}
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Focus PME */}
      <div className="bg-gradient-to-br from-purple-500/20 to-indigo-500/20 rounded-xl p-6 border-2 border-purple-500/30">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Target className="w-6 h-6 text-purple-400" />
          Focus PME 2026
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <h5 className="text-sm font-bold text-purple-300 mb-2">~50 livrables structurants</h5>
            <p className="text-gray-300 text-sm">
              Livrables alignés avec les 8 axes du département, priorisés selon l'impact business et la faisabilité technique.
            </p>
          </div>
          <div>
            <h5 className="text-sm font-bold text-purple-300 mb-2">20 no-brainers</h5>
            <p className="text-gray-300 text-sm">
              Actions rapides à haut impact : documentation, formations, intégrations simples, améliorations UX.
            </p>
          </div>
        </div>
        <div className="mt-4 pt-4 border-t border-purple-500/30">
          <h5 className="text-sm font-bold text-purple-300 mb-3">Éléments clés mis en évidence dans la roadmap :</h5>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="flex items-center gap-2 text-gray-300 text-sm">
              <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0" />
              <span><strong className="text-white">PRIsME Employé</strong> — Déploiement complet</span>
            </div>
            <div className="flex items-center gap-2 text-gray-300 text-sm">
              <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0" />
              <span><strong className="text-white">Outil projet intégré</strong> (inspiré PMBOK)</span>
            </div>
            <div className="flex items-center gap-2 text-gray-300 text-sm">
              <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0" />
              <span><strong className="text-white">Catalogue d'actifs</strong> — Recherche performante</span>
            </div>
            <div className="flex items-center gap-2 text-gray-300 text-sm">
              <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0" />
              <span><strong className="text-white">BOT PRIsME</strong> — Automatisation opérationnelle</span>
            </div>
          </div>
        </div>
      </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// ONGLET 6 — GOUVERNANCE & RITUELS
// ============================================================================
function RituelsTab() {
  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-600/20 via-purple-600/20 to-indigo-600/20 rounded-xl p-6 border-2 border-indigo-500/50">
        <h3 className="text-2xl font-bold text-center text-white mb-2">
          Gouvernance & rituels
        </h3>
        <p className="text-gray-300 text-center text-sm">
          Cadence, règles de décision et mécanismes de gouvernance
        </p>
      </div>

      {/* Cadence des rencontres */}
      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Calendar className="w-6 h-6 text-indigo-400" />
          Cadence des rencontres — Direction Programmation
        </h4>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Direction Programmation</strong> : 1 fois par mois
              <p className="text-sm text-gray-400 mt-1">Réunion mensuelle de la direction (Eric Durand, Guillaume Cloutier, Marc-Antoine Caron)</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Dany Bourassa</strong> : Présent 1 fois par trimestre (sauf exception et début)
              <p className="text-sm text-gray-400 mt-1">Présence exceptionnelle au démarrage pour établir les bases, puis présence trimestrielle pour alignement stratégique</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Ordre du jour</strong> : Préparé par Guillaume Cloutier
              <p className="text-gray-400 text-sm mt-1">Guillaume structure l'ordre du jour et distribue les sujets à traiter</p>
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Compte-rendu</strong> : Point obligatoire dans l'ordre du jour de chaque réunion
              <p className="text-gray-400 text-sm mt-1">Rédigé pendant la réunion, partagé avec tous les membres immédiatement après</p>
            </div>
          </li>
        </ul>
      </div>

      {/* Règles de décision */}
      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Shield className="w-6 h-6 text-indigo-400" />
          Règles de décision — Direction Programmation
        </h4>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Décisions stratégiques</strong> : <strong className="text-purple-300">Dany Bourassa</strong> décide, Direction consultée
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Décisions tactiques</strong> : <strong className="text-emerald-300">Éric Durand</strong> (Directeur) décide, Direction consultée
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Décisions opérationnelles</strong> : <strong className="text-blue-300">Guillaume Cloutier</strong> décide, Direction consultée
            </div>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-orange-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Décisions techniques</strong> : <strong className="text-orange-300">Marc-Antoine Caron (Marco)</strong> responsable (R), <strong className="text-emerald-300">Éric Durand</strong> approuve (A)
            </div>
          </li>
        </ul>
      </div>

      {/* Canal direct vers Dany */}
      <div className="bg-gradient-to-br from-purple-500/20 to-indigo-500/20 rounded-xl p-6 border-2 border-purple-500/30">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Users className="w-6 h-6 text-purple-400" />
          Canal direct vers Dany — Décisions stratégiques
        </h4>
        <p className="text-gray-300 leading-relaxed mb-4">
          Le canal direct vers Dany est <strong className="text-white">réservé au rôle clé</strong> de la direction programmation : <strong className="text-emerald-300">Éric Durand</strong> (Directeur).
        </p>
        <p className="text-gray-300 leading-relaxed mb-4">
          Pour toute demande nécessitant l'attention de Dany (décisions stratégiques), utiliser le format <strong className="text-white">Decision Packet</strong> structuré :
        </p>
        
        {/* Moyens de contact selon le besoin */}
        <div className="mb-4 p-3 bg-purple-500/10 border border-purple-500/20 rounded-lg">
          <p className="text-purple-200 text-sm font-semibold mb-2">📞 Moyens de contact selon le besoin :</p>
          <ul className="space-y-1.5 text-gray-300 text-sm">
            <li className="flex items-start gap-2">
              <span className="text-red-400">🔴</span>
              <span><strong className="text-white">Urgent</strong> : Appel téléphonique</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-green-400">🟢</span>
              <span><strong className="text-white">Informer</strong> : Courriel OK</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-yellow-400">🟡</span>
              <span><strong className="text-white">Approbation</strong> : Demande de réunion avec Decision Packet</span>
            </li>
          </ul>
        </div>
        <ul className="space-y-2 text-gray-300 text-sm">
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
            <span><strong className="text-white">Contexte</strong> : Situation actuelle, enjeu, impact</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
            <span><strong className="text-white">Options</strong> : Alternatives considérées avec avantages/inconvénients</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
            <span><strong className="text-white">Recommandation</strong> : Option privilégiée avec justification</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-purple-400 flex-shrink-0 mt-0.5" />
            <span><strong className="text-white">Décision requise</strong> : Question claire et action attendue</span>
          </li>
        </ul>
        <p className="text-gray-300 leading-relaxed mt-4 text-sm italic">
          Ce format garantit une décision rapide et éclairée, tout en respectant le temps limité de Dany.
        </p>
        
        {/* Hint de réflexion avant escalade */}
        <div className="mt-6 p-4 bg-yellow-500/10 border-2 border-yellow-500/30 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-yellow-200 font-semibold mb-2">
                ⚡ Avant d'escalader vers Dany, posez-vous cette question :
              </p>
              <p className="text-yellow-100 text-sm leading-relaxed">
                <strong className="text-white">Est-ce qu'une autre personne dans l'organisation pourrait répondre à votre question ou besoin ?</strong>
              </p>
              <p className="text-yellow-200/80 text-xs mt-2 italic">
                Dany doit être réservé uniquement pour les décisions stratégiques qui nécessitent vraiment son expertise et son approbation.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quand escalader */}
      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <AlertCircle className="w-6 h-6 text-yellow-400" />
          Quand escalader vers Dany
        </h4>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <span><strong className="text-white">Impact organisationnel majeur</strong> : Changements affectant tous les départements ou l'ensemble de l'organisation</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <span><strong className="text-white">Investissement significatif</strong> : Dépenses &gt; 10k$ ou &gt; 40h de développement</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <span><strong className="text-white">Conflit de vision</strong> : Désaccord sur la direction stratégique du département Programmation & IA</span>
          </li>
          <li className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
            <span><strong className="text-white">Risque réputationnel</strong> : Décisions affectant l'image de BZ ou la confiance des clients</span>
          </li>
        </ul>
      </div>

      {/* Quand ne PAS escalader */}
      <div className="bg-white/5 rounded-xl p-6 border border-indigo-500/20">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <CheckCircle className="w-6 h-6 text-emerald-400" />
          Quand ne PAS escalader
        </h4>
        <ul className="space-y-3 text-gray-300">
          <li className="flex items-start gap-3">
            <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Décisions opérationnelles routinières</strong> : Guillaume Cloutier peut décider seul
            </div>
          </li>
          <li className="flex items-start gap-3">
            <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Décisions tactiques courantes</strong> : Directeur (Éric) gère avec la direction, pas besoin d'escalade
            </div>
          </li>
          <li className="flex items-start gap-3">
            <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Décisions techniques</strong> : Marco (R) décide, Directeur (Éric) approuve, pas besoin d'escalade
            </div>
          </li>
          <li className="flex items-start gap-3">
            <X className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <strong className="text-white">Documentation et formation</strong> : Processus standard, pas d'escalade
            </div>
          </li>
        </ul>
      </div>

      {/* Lien avec Shield / Decision Desk */}
      <div className="bg-gradient-to-br from-indigo-500/20 to-purple-500/20 rounded-xl p-6 border-2 border-indigo-500/30">
        <h4 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Shield className="w-6 h-6 text-indigo-400" />
          Shield : Conséquence ou objectif atteint ?
        </h4>
        <div className="mb-4 p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
          <p className="text-emerald-200 font-semibold mb-2">🎯 Objectif : Ne pas avoir besoin du Shield</p>
          <p className="text-gray-300 text-sm leading-relaxed">
            Si la gouvernance de la direction programmation est <strong className="text-white">vivante et respectée</strong>, 
            les décisions sont prises aux bons niveaux (stratégique → Dany, tactique → Éric, opérationnel → Guillaume, technique → Marco). 
            <strong className="text-emerald-300"> Pas besoin d'escalader vers Dany = pas besoin du Shield.</strong>
          </p>
        </div>
        <div className="mb-4 p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
          <p className="text-yellow-200 font-semibold mb-2">⚠️ Shield devient nécessaire si...</p>
          <p className="text-gray-300 text-sm leading-relaxed">
            Si la gouvernance n'est <strong className="text-white">pas respectée</strong> (escalades inappropriées, décisions non documentées, 
            surcharge de Dany), alors <strong className="text-yellow-300">Shield devient un pare-feu mental nécessaire</strong> pour protéger Dany 
            et structurer les décisions importantes.
          </p>
        </div>
        <p className="text-gray-300 leading-relaxed mb-4 text-sm">
          <strong className="text-white">En résumé</strong> : Une gouvernance vivante = moins d'escalades = moins besoin du Shield. 
          C'est la preuve que la direction programmation fonctionne de manière autonome et efficace.
        </p>
        <ul className="space-y-2 text-gray-300 text-sm">
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-indigo-400 flex-shrink-0 mt-0.5" />
            <span>Si escalade nécessaire → Shield Decision Desk (format Decision Packet)</span>
          </li>
          <li className="flex items-start gap-2">
            <CheckCircle className="w-4 h-4 text-indigo-400 flex-shrink-0 mt-0.5" />
            <span>Shield = pare-feu mental pour éviter la surcharge décisionnelle de Dany</span>
          </li>
        </ul>
      </div>
    </div>
  );
}
